import React, { useState, useEffect, useRef } from 'react';

// Extend window for demo globals used in dev/demo mode
declare global {
  interface Window {
    demoFingerprints?: Record<string, any>;
  }
}
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Upload, Database, Info, TrendingUp, AlertCircle, Check, Plus, Copy } from 'lucide-react';
import { usePlotsStore } from '@/store/plots';
import { useProjectsStore } from '@/store/projects';
import FileUpload from '@/components/upload/file-upload';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { apiRequest, authorizedFetch } from '@/lib/queryClient';
import { socket, subscribeProject } from '@/ws/socket';
import type { PlotArtifactsReadyPayload } from '@shared/ws';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { Virtuoso } from 'react-virtuoso';

// Guard against stale artifact responses when switching plots quickly
let __statsLoadEpoch = 0;

// Canonical stats containers
export type StatsJson = {
  group_stats?: any[];
  omnibus?: any[];
  pairwise?: any[];
  regression?: any[];
  descriptive?: any[];
  [key: string]: any; // Allow other fields for backward compatibility
};

export type NormalizedStatRow = {
  section: "group_stats" | "omnibus" | "pairwise" | "regression" | "descriptive";
  label: string;
  value: number | string | null;
  details: Record<string, unknown>;
};

export function normalizeStats(raw: StatsJson | null | undefined): NormalizedStatRow[] {
  if (!raw) return [];

  const sections: Array<"group_stats" | "omnibus" | "pairwise" | "regression" | "descriptive"> = [
    "group_stats",
    "omnibus",
    "pairwise",
    "regression",
    "descriptive",
  ];

  const rows: NormalizedStatRow[] = [];

  for (const section of sections) {
    const items = (raw as any)[section] ?? [];
    if (!Array.isArray(items)) continue;

    items.forEach((item, idx) => {
      rows.push({
        section,
        label:
          item.label ??
          item.stat_id ??
          item.name ??
          item.test ??
          item.method ??
          `${section}[${idx}]`,
        value:
          item.value ??
          item.estimate ??
          item.mean ??
          item.median ??
          item.statistic ??
          item.stat ??
          item.p ??
          item.p_value ??
          null,
        details: item,
      });
    });
  }

  return rows;
}

// Helper function to copy text to clipboard
async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch (err) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  }
}

// Format stats data as plain text matching the UI display
function formatStatsAsText(plotStats: any, figureSummary: any): string {
  const lines: string[] = [];
  
  lines.push('Methods & Results (from last run)');
  lines.push('');
  
  // N per group section
  const countsLegacy = plotStats?.n_per_group || plotStats?.group_counts || plotStats?.groupCounts;
  const countsX = plotStats?.n_by_x;
  const countsHue = plotStats?.n_by_hue;
  const nByPatterns: Array<{ key: string; value: any; label: string }> = [];
  
  if (plotStats && typeof plotStats === 'object') {
    for (const [k, v] of Object.entries(plotStats)) {
      if (k.startsWith('n_by_') && k !== 'n_by_x' && k !== 'n_by_hue' && v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length > 0) {
        const fieldName = k.replace('n_by_', '').replace(/_/g, ' ');
        nByPatterns.push({ key: k, value: v, label: `n by ${fieldName}` });
      }
    }
  }
  
  const hasAny = (obj: any) => obj && typeof obj === 'object' && Object.keys(obj).length > 0;
  
  // Determine which n_by field to use (prioritize n_by_x, then n_by_hue, then legacy)
  let primaryNBy: { label: string; data: any } | null = null;
  
  if (hasAny(countsX)) {
    primaryNBy = { label: 'n by program', data: countsX };
  } else if (hasAny(countsHue)) {
    primaryNBy = { label: 'n by program', data: countsHue };
  } else if (hasAny(countsLegacy)) {
    primaryNBy = { label: 'n by program', data: countsLegacy };
  } else if (nByPatterns.length > 0) {
    primaryNBy = { label: nByPatterns[0].label, data: nByPatterns[0].value };
  }
  
  if (primaryNBy) {
    lines.push(primaryNBy.label);
    Object.entries(primaryNBy.data).forEach(([g, n]: any) => {
      lines.push(`${String(g)} (n=${Number(n) || 0})`);
    });
    lines.push('');
  }
  
  // Additional statistics section
  const fmt = (v: any): string => {
    if (typeof v === 'number' && isFinite(v)) {
      if (v < 1e-3) return Number(v).toExponential(2);
      return Number(v).toPrecision(3);
    }
    return String(v ?? '');
  };
  
  // Check if we have any additional statistics to show
  const hasAdditionalStats = plotStats?.ols_fits || plotStats?.ols || plotStats?.pearson_corrs || plotStats?.pearson || plotStats?.ancova;
  
  if (hasAdditionalStats) {
    lines.push('Additional statistics');
    lines.push('');
  }
  
  // OLS fits
  const olsFits = plotStats?.ols_fits || plotStats?.ols || {};
  if (olsFits && typeof olsFits === 'object' && Object.keys(olsFits).length > 0) {
    lines.push('ols fits');
    Object.entries(olsFits).forEach(([key, fit]: any) => {
      if (fit && typeof fit === 'object') {
        lines.push(key);
        if (fit.slope !== undefined) {
          lines.push('Slope');
          lines.push(fmt(fit.slope));
        }
        if (fit.intercept !== undefined) {
          lines.push('Intercept');
          lines.push(fmt(fit.intercept));
        }
        if (fit.r !== undefined) {
          lines.push('R');
          lines.push(fmt(fit.r));
        }
        if (fit.p !== undefined || fit.p_value !== undefined) {
          lines.push('P');
          lines.push(fmt(fit.p ?? fit.p_value));
        }
        if (fit.stderr !== undefined || fit.stderr_slope !== undefined) {
          lines.push('Stderr');
          lines.push(fmt(fit.stderr ?? fit.stderr_slope));
        }
        lines.push('');
      }
    });
  }
  
  // Pearson correlations
  const pearsonCorrs = plotStats?.pearson_corrs || plotStats?.pearson || {};
  if (pearsonCorrs && typeof pearsonCorrs === 'object' && Object.keys(pearsonCorrs).length > 0) {
    lines.push('pearson corrs');
    Object.entries(pearsonCorrs).forEach(([key, corr]: any) => {
      if (corr && typeof corr === 'object') {
        lines.push(key);
        if (corr.r !== undefined) {
          lines.push('R');
          lines.push(fmt(corr.r));
        }
        if (corr.p !== undefined || corr.p_value !== undefined) {
          lines.push('P');
          lines.push(fmt(corr.p ?? corr.p_value));
        }
        if (corr.ci_low !== undefined || corr.ci_low_95 !== undefined) {
          lines.push('Ci Low');
          lines.push(fmt(corr.ci_low ?? corr.ci_low_95));
        }
        if (corr.ci_high !== undefined || corr.ci_high_95 !== undefined) {
          lines.push('Ci High');
          lines.push(fmt(corr.ci_high ?? corr.ci_high_95));
        }
        lines.push('');
      }
    });
  }
  
  // ANCOVA
  const ancova = plotStats?.ancova || {};
  if (ancova && typeof ancova === 'object' && Object.keys(ancova).length > 0) {
    lines.push('ancova');
    if (ancova.F !== undefined) {
      lines.push('F');
      lines.push(fmt(ancova.F));
    }
    if (ancova.p !== undefined || ancova.p_value !== undefined) {
      lines.push('P');
      lines.push(fmt(ancova.p ?? ancova.p_value));
    }
    if (ancova.eta2 !== undefined || ancova.eta_squared !== undefined) {
      lines.push('Eta2');
      lines.push(fmt(ancova.eta2 ?? ancova.eta_squared));
    }
    lines.push('');
  }
  
  // Generic additional statistics (other keys)
  const known = new Set<string>([
    'alpha','n','n_points','methods','tests','computed_tests',
    'n_by_x','n_by_hue','n_per_group','group_counts','groupCounts',
    'species_order','group_order','category_order','legend_order',
    'group_summaries','group_stats',
    'omnibus','overall','overall_hue','kruskal',
    'pairwise','pairwise_kept','pairwise_mode',
    'ttest_ind','ttest_rel','welch','mannwhitney','wilcoxon',
    'anova','rm_anova','lmm','chi2','fisher',
    'ks_2samp','anderson','permutation','logistic','poisson','negbin','zinb','coxph','gam',
    'cohens_d','hedges_g','glass_delta','cliffs_delta','r_rb','odds_ratio','eta2','partial_eta2','r','r_squared',
    'effect_size_interpretation','min_effect',
    'correlation','pearson','spearman','kendall','cramers_v','phi','contingency_coefficient',
    'trend','trend_summaries','regression',
    'multiple_comparisons','top_k_pairs','top_k_effects',
    'stats_display','statistical_annotations','ci_method','ci_level','ci','show_ci',
    'n_missing','n_present','missing_rate','imputation_method',
    'outlier_count','outlier_method','duplicate_count','data_quality_score',
    'log2_fc','adj_p_value','base_mean','enrichment_score',
    'autocorrelation','seasonality','trend_strength',
    'hazard_ratio','survival_curves','adverse_events',
    'effect_sizes','meta_analysis','heterogeneity','neg_log10_p','significance_threshold',
    'clustering_method','distance_metric','dendrogram_order',
    'ols_fits','ols','pearson_corrs','ancova'
  ]);
  
  const extra = Object.entries(plotStats || {}).filter(([k, v]) => {
    const key = String(k);
    return !known.has(key) && !key.startsWith('n_by_') && v !== null && v !== undefined && (typeof v !== 'object' || Object.keys(v || {}).length > 0);
  });
  
  if (extra.length > 0) {
    extra.forEach(([key, val]: any) => {
      const keyLabel = String(key).split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      lines.push(keyLabel);
      if (typeof val === 'object' && !Array.isArray(val)) {
        Object.entries(val).forEach(([kk, vv]: any) => {
          const subKeyLabel = String(kk).split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
          lines.push(`${subKeyLabel}\n${fmt(vv)}`);
        });
      } else {
        lines.push(fmt(val));
      }
      lines.push('');
    });
  }
  
  return lines.join('\n');
}

interface DataTabProps {
  onDataReplaced?: () => void;
}

export default function DataTab({ onDataReplaced }: DataTabProps) {
  const { toast } = useToast();
  const [showUpload, setShowUpload] = useState(false);
  const [copiedStats, setCopiedStats] = useState(false);
  const copiedStatsTimeoutRef = useRef<number | null>(null);
  const [dataPreview, setDataPreview] = useState<any[]>([]);
  const [dataSummary, setDataSummary] = useState<any>(null);
  const [statisticalAnalysis, setStatisticalAnalysis] = useState<any>(null);
  const [plotStats, setPlotStats] = useState<any>(null);
  const [isLoadingPlotStats, setIsLoadingPlotStats] = useState<boolean>(false);
  const [figureSummary, setFigureSummary] = useState<any>(null);
  const [isLoadingFigure, setIsLoadingFigure] = useState<boolean>(false); // retained for UI state hooks
  const [plotlyFigure, setPlotlyFigure] = useState<any>(null);
  const [isLoadingPlotlyFigure, setIsLoadingPlotlyFigure] = useState<boolean>(false); // retained for UI state hooks
  const [usingDraftArtifacts, setUsingDraftArtifacts] = useState<boolean>(false);
  const [draftToastForPlot, setDraftToastForPlot] = useState<string | null>(null);
  const [isSelectedPending, setIsSelectedPending] = useState<boolean>(false);
  const didWarnPendingOverlapRef = useRef<boolean>(false);
  const seenVersionsRef = useRef<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'data'|'summary'|'statistics'>('data');
  const featurePreview = String((import.meta as any).env?.VITE_FEATURE_DATA_PREVIEW_ENDPOINT || 'true').toLowerCase() !== 'false';
  // Default virtualization OFF to preserve existing UI unless explicitly enabled
  const featureVirtual = String((import.meta as any).env?.VITE_FEATURE_DATA_VIRTUAL_TABLE || 'false').toLowerCase() === 'true';
  const buildPreviewUrl = (fileId: string) => featurePreview ? `/api/files/${fileId}/preview?rows=100` : `/api/files/${fileId}/data`;

  const resolveDraftContext = () => {
    if (typeof window === 'undefined') {
      return { projectId: currentProject?.id || null, draftId: null } as { projectId: string | null; draftId: string | null };
    }
    const segments = window.location.pathname.split('/').filter(Boolean);
    const editorIdx = segments.indexOf('plot-editor');
    const projectFromPath = editorIdx >= 0 ? segments[editorIdx + 1] : null;
    const draftFromPath = editorIdx >= 0 ? segments[editorIdx + 2] : null;
    return {
      projectId: (currentProject?.id || projectFromPath || null) as string | null,
      draftId: (draftFromPath || null) as string | null,
    };
  };

  const fetchArtifactJson = async (path: string, init?: any): Promise<any | null> => {
    try {
      const res = await authorizedFetch(path, { allow404: true, ...(init || {}) });
      if (!res || !res.ok) {
        if (res && res.status && res.status !== 404) {
          try { toast({ title: 'Error loading artifacts', description: `Failed to fetch ${path} (${res.status})` }); } catch {}
        }
        return null;
      }
      const text = await res.text();
      if (!String(text || '').trim()) return null;
      try { return JSON.parse(text); } catch { return null; }
    } catch {
      try { toast({ title: 'Network error', description: `Failed to fetch ${path}` }); } catch {}
      return null;
    }
  };

  // Bounded backoff for 404 artifact races
  const fetchWithBackoff = async (path: string, maxAttempts = 5): Promise<any | null> => {
    const delays = [500, 1000, 1500, 2000, 2500];
    for (let i = 0; i < Math.min(maxAttempts, delays.length); i++) {
      try {
        const res = await authorizedFetch(path, { allow404: true });
        if (res && res.ok) {
          const txt = await res.text();
          if (!txt) return null;
          try { return JSON.parse(txt); } catch { return null; }
        }
        // Stop immediately on non-404 errors
        if (res && !res.ok && res.status && res.status !== 404) {
          try { toast({ title: 'Error loading artifacts', description: `Failed to fetch ${path} (${res.status})` }); } catch {}
          return null;
        }
        // Stop retrying on 404 after first attempt to avoid noisy logs when metadata is missing by design
        if (res && res.status === 404) {
          return null;
        }
      } catch {
        try { toast({ title: 'Network error', description: `Failed to fetch ${path}` }); } catch {}
        return null;
      }
      await new Promise((r) => setTimeout(r, delays[i]));
    }
    return null;
  };

  const fetchDraftArtifactJson = async (file: string, init?: any): Promise<any | null> => {
    const ctx = resolveDraftContext();
    if (!ctx.projectId || !ctx.draftId) return null;
    return fetchArtifactJson(`/api/ai/v2/drafts/${ctx.projectId}/${ctx.draftId}/${file}`, init);
  };

  const normalizeTestRecord = (r: any): any => {
    if (!r || typeof r !== "object") return r;

    const out: any = { ...r };

    // Test name & method
    out.test = out.test ?? out.test_type ?? out.method ?? null;
    if (!out.method && out.test) out.method = out.test;

    // Statistic
    const tStat = out.t_stat ?? out.t;
    const fStat = out.F_stat ?? out.F ?? out.f;
    const zStat = out.z_stat ?? out.z;
    const chi2 = out.chi2 ?? out.chisq ?? out.chi_sq;
    out.t_stat = tStat ?? out.t_stat ?? null;
    out.F_stat = fStat ?? out.F_stat ?? null;
    out.z_stat = zStat ?? out.z_stat ?? null;
    out.chi2 = chi2 ?? out.chi2 ?? null;
    out.statistic =
      out.statistic ??
      out.t_stat ??
      out.F_stat ??
      out.z_stat ??
      out.chi2 ??
      null;

    // Degrees of freedom
    out.df1 = out.df1 ?? out.df_num ?? null;
    out.df2 = out.df2 ?? out.df_den ?? null;
    out.df =
      out.df ??
      out.dof ??
      (out.df1 != null && out.df2 == null ? out.df1 : null);

    // P-values
    out.p =
      out.p ??
      out.p_value ??
      out.pval ??
      out.p_val ??
      out.pvalue ??
      null;
    out.p_adj =
      out.p_adj ??
      out.p_adjusted ??
      out.p_corrected ??
      out.q ??
      out.q_value ??
      null;

    // Generic effect size
    out.effect_size =
      out.effect_size ??
      out.cohens_d ??
      out.d ??
      out.eta_sq ??
      out.partial_eta_sq ??
      out.odds_ratio ??
      out.hazard_ratio ??
      out.risk_ratio ??
      null;

    // CI
    out.ci_low =
      out.ci_low ??
      out.ci95_low ??
      out.ci_95_low ??
      out.ci_low_95 ??
      out.ci_lower ??
      out.lower_ci ??
      null;
    out.ci_high =
      out.ci_high ??
      out.ci95_high ??
      out.ci_95_high ??
      out.ci_high_95 ??
      out.ci_upper ??
      out.upper_ci ??
      null;

    return out;
  };

  // Compatibility shim: normalize stats.json shapes
  const normalizeStatsJson = (stats: any): any => {
    try {
      if (!stats || typeof stats !== 'object') return stats;
      const out: any = { ...stats };

      // Fill n_per_group from group_stats when absent
      if (!out.n_per_group && out.group_stats) {
        try {
          const nMap: Record<string, number> = {};
          if (Array.isArray(out.group_stats)) {
            for (const row of out.group_stats) {
              const key = String(row?.group || row?.category || row?.x || row?.GeneID || row?.gene || '');
              if (key) {
                const nnum = Number((row as any)?.n);
                if (Number.isFinite(nnum)) nMap[key] = nnum;
              }
            }
          } else if (out.group_stats && typeof out.group_stats === 'object') {
            for (const [k, v] of Object.entries(out.group_stats)) {
              const nnum = Number((v as any)?.n);
              if (Number.isFinite(nnum)) nMap[String(k)] = nnum;
            }
          }
          if (Object.keys(nMap).length > 0) out.n_per_group = nMap;
        } catch {}
      }

      // Synthesize legacy-friendly group_summaries when only group_stats is present
      if (!out.group_summaries && out.group_stats) {
        if (Array.isArray(out.group_stats)) {
          out.group_summaries = (out.group_stats as any[]).map((r: any) => ({
            ...r,
            // Normalize CI fields
            ci_low: r?.ci_low ?? r?.ci95_low ?? r?.ci_95_low ?? r?.ci_low_95 ?? r?.ci_lower ?? r?.lower_ci ?? null,
            ci_high: r?.ci_high ?? r?.ci95_high ?? r?.ci_95_high ?? r?.ci_high_95 ?? r?.ci_upper ?? r?.upper_ci ?? null,
            se: r?.se ?? r?.sem,
            // Normalize statistical result fields (preserve all types)
            odds_ratio: r?.odds_ratio ?? r?.or ?? r?.OR ?? r?.oddsRatio ?? null,
            hazard_ratio: r?.hazard_ratio ?? r?.hr ?? r?.HR ?? r?.hazardRatio ?? null,
            risk_ratio: r?.risk_ratio ?? r?.rr ?? r?.RR ?? r?.riskRatio ?? null,
            mean_difference: r?.mean_difference ?? r?.mean_diff ?? r?.md ?? r?.MD ?? r?.diff ?? null,
            cohens_d: r?.cohens_d ?? r?.cohensD ?? r?.d ?? null,
            hedges_g: r?.hedges_g ?? r?.hedgesG ?? r?.g ?? null,
            cliffs_delta: r?.cliffs_delta ?? r?.cliffsDelta ?? r?.delta ?? null,
            effect_size: r?.effect_size ?? r?.effectSize ?? null,
            r: r?.r ?? r?.pearson_r ?? r?.spearman_r ?? r?.kendall_r ?? r?.correlation ?? r?.corr ?? null,
            pearson_r: r?.pearson_r ?? null,
            spearman_r: r?.spearman_r ?? null,
            kendall_r: r?.kendall_r ?? null,
            beta: r?.beta ?? r?.coefficient ?? r?.coef ?? r?.slope ?? null,
            p: r?.p ?? r?.p_value ?? r?.pval ?? r?.p_val ?? null,
          }));
        } else if (out.group_stats && typeof out.group_stats === 'object') {
          const mapped: Record<string, any> = {};
          for (const [k, v] of Object.entries(out.group_stats)) {
            const vv: any = v || {};
            mapped[String(k)] = {
              // Descriptive statistics
              ...(vv.mean != null ? { mean: vv.mean } : {}),
              ...(vv.median != null ? { median: vv.median } : {}),
              ...(vv.q1 != null ? { q1: vv.q1 } : {}),
              ...(vv.q3 != null ? { q3: vv.q3 } : {}),
              ...(vv.std != null ? { std: vv.std } : {}),
              ...(vv.sem != null ? { se: vv.sem } : (vv.se != null ? { se: vv.se } : {})),
              ...(vv.n != null ? { n: vv.n } : {}),
              // Confidence intervals
              ci_low: vv?.ci_low ?? vv?.ci95_low ?? vv?.ci_95_low ?? vv?.ci_low_95 ?? vv?.ci_lower ?? vv?.lower_ci ?? null,
              ci_high: vv?.ci_high ?? vv?.ci95_high ?? vv?.ci_95_high ?? vv?.ci_high_95 ?? vv?.ci_upper ?? vv?.upper_ci ?? null,
              // Statistical results - preserve all types
              ...(vv.odds_ratio != null ? { odds_ratio: vv.odds_ratio } : (vv.or != null ? { odds_ratio: vv.or } : (vv.OR != null ? { odds_ratio: vv.OR } : {}))),
              ...(vv.hazard_ratio != null ? { hazard_ratio: vv.hazard_ratio } : (vv.hr != null ? { hazard_ratio: vv.hr } : (vv.HR != null ? { hazard_ratio: vv.HR } : {}))),
              ...(vv.risk_ratio != null ? { risk_ratio: vv.risk_ratio } : (vv.rr != null ? { risk_ratio: vv.rr } : (vv.RR != null ? { risk_ratio: vv.RR } : {}))),
              ...(vv.mean_difference != null ? { mean_difference: vv.mean_difference } : (vv.mean_diff != null ? { mean_difference: vv.mean_diff } : (vv.md != null ? { mean_difference: vv.md } : (vv.MD != null ? { mean_difference: vv.MD } : {})))),
              ...(vv.cohens_d != null ? { cohens_d: vv.cohens_d } : (vv.cohensD != null ? { cohens_d: vv.cohensD } : (vv.d != null && vv.effect_size == null ? { cohens_d: vv.d } : {}))),
              ...(vv.hedges_g != null ? { hedges_g: vv.hedges_g } : (vv.hedgesG != null ? { hedges_g: vv.hedgesG } : (vv.g != null ? { hedges_g: vv.g } : {}))),
              ...(vv.cliffs_delta != null ? { cliffs_delta: vv.cliffs_delta } : (vv.cliffsDelta != null ? { cliffs_delta: vv.cliffsDelta } : (vv.delta != null ? { cliffs_delta: vv.delta } : {}))),
              ...(vv.effect_size != null ? { effect_size: vv.effect_size } : (vv.effectSize != null ? { effect_size: vv.effectSize } : {})),
              ...(vv.r != null ? { r: vv.r } : {}),
              ...(vv.pearson_r != null ? { pearson_r: vv.pearson_r } : {}),
              ...(vv.spearman_r != null ? { spearman_r: vv.spearman_r } : {}),
              ...(vv.kendall_r != null ? { kendall_r: vv.kendall_r } : {}),
              ...(vv.beta != null ? { beta: vv.beta } : {}),
              ...(vv.coefficient != null ? { coefficient: vv.coefficient } : (vv.coef != null ? { coefficient: vv.coef } : {})),
              ...(vv.slope != null ? { slope: vv.slope } : {}),
              // P-values
              ...(vv.p != null ? { p: vv.p } : (vv.p_value != null ? { p: vv.p_value } : (vv.pval != null ? { p: vv.pval } : (vv.p_val != null ? { p: vv.p_val } : {})))),
            };
          }
          out.group_summaries = mapped;
        }
      }

      // Normalize test containers - ensure they are arrays
      const normalizeTestArray = (arr: any): any[] => {
        if (!arr) return [];
        if (Array.isArray(arr)) {
          return arr.map((r) => normalizeTestRecord(r));
        }
        // Convert single object to array
        if (typeof arr === 'object') {
          return [normalizeTestRecord(arr)];
        }
        return [];
      };

      // Ensure canonical containers are arrays
      out.omnibus = normalizeTestArray(out.omnibus);
      out.pairwise = normalizeTestArray(out.pairwise);
      out.regression = normalizeTestArray(out.regression);
      out.descriptive = normalizeTestArray(out.descriptive);
      
      // Normalize regression structure (can have nested model_test and coefficients)
      if (Array.isArray(out.regression)) {
        out.regression = out.regression.map((m: any) => {
          const mm: any = { ...m };
          if (mm.model_test) {
            mm.model_test = normalizeTestRecord(mm.model_test);
          }
          if (Array.isArray(mm.coefficients)) {
            mm.coefficients = mm.coefficients.map((c: any) =>
              normalizeTestRecord(c)
            );
          }
          return mm;
        });
      }

      return out;
    } catch {
      return stats;
    }
  };

  const loadArtifactsForPlot = async (
    plotId: string,
    options: { allowDraftFallback?: boolean; signal?: { cancelled: boolean } } = {}
  ) => {
    const { allowDraftFallback = true, signal } = options;
    const cancelled = () => Boolean(signal?.cancelled);
    const myEpoch = ++__statsLoadEpoch;
    const controller = new AbortController();
    const abortOpts = { signal: controller.signal } as const;
    let draftUsed = false;

    setIsLoadingPlotStats(true);
    try {
      if ((currentPlot as any)?.statsJson && !cancelled()) {
        if (myEpoch === __statsLoadEpoch) setPlotStats(normalizeStatsJson((currentPlot as any).statsJson));
      }
      // Try latest stats first; on failure, fall back to draft when allowed
      try {
        const statsLatest = await fetchArtifactJson(`/api/ai/v2/plots/${plotId}/versions/latest/stats.json`, abortOpts);
        if (cancelled()) return;
        if (statsLatest) {
          if (myEpoch === __statsLoadEpoch) setPlotStats(normalizeStatsJson(statsLatest));
        } else if (allowDraftFallback) {
          const statsDraft = await fetchDraftArtifactJson('stats.json', abortOpts);
          if (cancelled()) return;
          if (statsDraft) {
            if (myEpoch === __statsLoadEpoch) setPlotStats(normalizeStatsJson(statsDraft));
            draftUsed = true;
          } else if (myEpoch === __statsLoadEpoch) {
            setPlotStats(null);
          }
        } else if (!cancelled() && myEpoch === __statsLoadEpoch) {
          setPlotStats(null);
        }
      } catch {
        if (allowDraftFallback) {
          const statsDraft = await fetchDraftArtifactJson('stats.json', abortOpts);
          if (cancelled()) return;
          if (statsDraft) {
            if (myEpoch === __statsLoadEpoch) setPlotStats(normalizeStatsJson(statsDraft));
            draftUsed = true;
          } else if (myEpoch === __statsLoadEpoch) {
            setPlotStats(null);
          }
        } else if (!cancelled() && myEpoch === __statsLoadEpoch) {
          setPlotStats(null);
        }
      }
    } finally {
      if (!cancelled()) {
        setIsLoadingPlotStats(false);
      }
    }

    setIsLoadingFigure(true);
    try {
      const figureLatest = await fetchArtifactJson(`/api/ai/v2/plots/${plotId}/versions/latest/figure.json`, abortOpts);
      if (cancelled()) return;
      if (figureLatest) {
        if (myEpoch === __statsLoadEpoch) setFigureSummary(figureLatest);
      } else if (allowDraftFallback) {
        const figureDraft = await fetchDraftArtifactJson('figure.json', abortOpts);
        if (cancelled()) return;
        if (figureDraft) {
          if (myEpoch === __statsLoadEpoch) setFigureSummary(figureDraft);
          draftUsed = true;
        } else {
          if (myEpoch === __statsLoadEpoch) setFigureSummary(null);
        }
      } else if (!cancelled()) {
        if (myEpoch === __statsLoadEpoch) setFigureSummary(null);
      }
    } finally {
      if (!cancelled()) {
        setIsLoadingFigure(false);
      }
    }

    setIsLoadingPlotlyFigure(true);
    try {
      if ((currentPlot as any)?.plotlyJson && !cancelled()) {
        if (myEpoch === __statsLoadEpoch) setPlotlyFigure((currentPlot as any).plotlyJson);
      }
      const plotlyLatest = await fetchArtifactJson(`/api/ai/v2/plots/${plotId}/versions/latest/plotly_figure.json`, abortOpts);
      if (cancelled()) return;
      if (plotlyLatest) {
        if (myEpoch === __statsLoadEpoch) setPlotlyFigure(plotlyLatest);
      } else if (allowDraftFallback) {
        const plotlyDraft = await fetchDraftArtifactJson('plotly_figure.json', abortOpts);
        if (cancelled()) return;
        if (plotlyDraft) {
          if (myEpoch === __statsLoadEpoch) setPlotlyFigure(plotlyDraft);
          draftUsed = true;
        } else {
          if (myEpoch === __statsLoadEpoch) setPlotlyFigure(null);
        }
      } else if (!cancelled()) {
        if (myEpoch === __statsLoadEpoch) setPlotlyFigure(null);
      }
    } finally {
      if (!cancelled()) {
        setIsLoadingPlotlyFigure(false);
      }
    }

    try { controller.abort(); } catch {}
    if (!cancelled()) {
      setUsingDraftArtifacts(draftUsed);
    }
  };

  // Lightweight normal helpers for meta-analysis fallback (Stouffer)
  const erf = (x: number) => {
    // Abramowitz & Stegun formula 7.1.26
    const sign = x < 0 ? -1 : 1;
    const ax = Math.abs(x);
    const p = 0.3275911;
    const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741, a4 = -1.453152027, a5 = 1.061405429;
    const t = 1 / (1 + p * ax);
    const y = 1 - (((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t) * Math.exp(-ax * ax);
    return sign * y;
  };
  const normCdf = (z: number) => 0.5 * (1 + erf(z / Math.SQRT2));
  const invNorm = (p: number) => {
    // Acklam's approximation for inverse normal CDF
    // Clamp
    const pp = Math.min(1 - 1e-16, Math.max(1e-16, p));
    const a = [-39.6968302866538, 220.946098424521, -275.928510446969, 138.357751867269, -30.6647980661472, 2.50662827745924];
    const b = [-54.4760987982241, 161.585836858041, -155.698979859887, 66.8013118877197, -13.2806815528857];
    const c = [-0.00778489400243029, -0.322396458041136, -2.40075827716184, -2.54973253934373, 4.37466414146497, 2.93816398269878];
    const d = [0.00778469570904146, 0.32246712907004, 2.445134137143, 3.75440866190742];
    const plow = 0.02425, phigh = 1 - plow;
    let q, r;
    if (pp < plow) {
      q = Math.sqrt(-2 * Math.log(pp));
      return (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
        ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
    } else if (pp > phigh) {
      q = Math.sqrt(-2 * Math.log(1 - pp));
      return -(((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
        ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
    }
    q = pp - 0.5; r = q * q;
    return (((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * q /
      (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1);
  };
  const [virtualWindow, setVirtualWindow] = useState({ start: 0, end: 100 });
  const { currentPlot, editorMode, setLiveDataFingerprint, markFingerprintHydrated, isFingerprintHydrated } = usePlotsStore();
  const { currentProject } = useProjectsStore();
  const [localFingerprintId, setLocalFingerprintId] = useState<string | null>(null);
  const inflightAnalyzeByFileRef = useRef<Record<string, Promise<void> | undefined>>({});
  const hydratedLoggedRef = useRef<Set<string>>(new Set());
  

  //

  // Standardized chips for tags/badges in the panel
  const Chip = ({ children }: { children: React.ReactNode }) => (
    <Badge variant="secondary" className="rounded-md text-xs font-normal px-2 py-0.5">{children}</Badge>
  );
  

  // React to cross-app analysis events – keep local id in sync
  useEffect(() => {
    const onSelected = (e: any) => {
      try {
        if ((import.meta as any).env?.VITE_NAV_DEBUG) console.log('[dataSelected]', e?.detail);
      } catch {}
      setIsSelectedPending(true);
    };
    const onAnalyzed = (e: any) => {
      try {
        if ((import.meta as any).env?.VITE_NAV_DEBUG) console.log('[dataAnalyzed]', e?.detail);
        const d = e?.detail || {};
        if (d.fingerprintId) {
          setLocalFingerprintId(d.fingerprintId);
          if (d.fingerprint && typeof window !== 'undefined') {
            window.demoFingerprints = window.demoFingerprints || {};
            window.demoFingerprints[d.fingerprintId] = d.fingerprint;
          }
          // Fetch canonical hydrated fingerprint by fileId to avoid stale client copies
          if (d.fileId) {
            (async () => {
              try {
                const fpResp = await apiRequest('GET', `/api/ai/v2/fingerprint/${d.fileId}`);
                if (fpResp.ok) {
                  const fpJson = await fpResp.json();
                  if (fpJson?.success && fpJson?.fingerprint) {
                    window.demoFingerprints = window.demoFingerprints || {};
                    window.demoFingerprints[fpJson.fingerprintId] = fpJson.fingerprint;
                    setLocalFingerprintId(fpJson.fingerprintId);
                  }
                }
              } catch {}
            })();
          }
        }
      } catch {}
      // Clear selected-pending flag; analyzed view will render
      setIsSelectedPending(false);
    };
    window.addEventListener('lumi:dataSelected' as any, onSelected as any);
    window.addEventListener('lumi:dataAnalyzed' as any, onAnalyzed as any);
    return () => {
      window.removeEventListener('lumi:dataSelected' as any, onSelected as any);
      window.removeEventListener('lumi:dataAnalyzed' as any, onAnalyzed as any);
    };
  }, []);

  // One-time warning: selected pending but old analyzed view still visible
  useEffect(() => {
    try {
      if (isSelectedPending && (dataSummary || plotStats || figureSummary || plotlyFigure)) {
        if (!didWarnPendingOverlapRef.current) {
          didWarnPendingOverlapRef.current = true;
          console.warn('[data-tab] isSelectedPending true while analyzed view visible');
        }
      } else if (!isSelectedPending) {
        didWarnPendingOverlapRef.current = false;
      }
    } catch {}
  }, [isSelectedPending, dataSummary, plotStats, figureSummary, plotlyFigure]);

  // Clear cached fingerprint when editor unbinds
  useEffect(() => {
    try {
      if (editorMode !== 'bound') {
        setLocalFingerprintId(null);
        setDataPreview([]);
        setDataSummary(null);
        setStatisticalAnalysis(null);
        setPlotStats(null);
        setFigureSummary(null);
        setPlotlyFigure(null);
      }
    } catch {}
  }, [editorMode]);

  // Listen for explicit session resets to clear caches immediately
  useEffect(() => {
    const onReset = () => {
      try {
        setLocalFingerprintId(null);
        setDataPreview([]);
        setDataSummary(null);
        setStatisticalAnalysis(null);
        setPlotStats(null);
        setFigureSummary(null);
        setPlotlyFigure(null);
        setUsingDraftArtifacts(false);
        setDraftToastForPlot(null);
      } catch {}
    };
    window.addEventListener('lumi:sessionReset' as any, onReset as any);
    return () => window.removeEventListener('lumi:sessionReset' as any, onReset as any);
  }, []);

  // React to store-backed fingerprint changes for the active context
  useEffect(() => {
    const onStoreFp = (e: any) => {
      try {
        const d = (e?.detail || {}) as any;
        const pid = String(currentProject?.id || (currentPlot as any)?.projectId || '');
        const cid = String((currentPlot as any)?.id || '');
        if (!pid || !cid) return;
        if (String(d.projectId) !== pid || String(d.plotId) !== cid) return;
        setLocalFingerprintId(d.fingerprintId || null);
      } catch {}
    };
    window.addEventListener('store:liveDataFingerprintChanged' as any, onStoreFp as any);
    return () => window.removeEventListener('store:liveDataFingerprintChanged' as any, onStoreFp as any);
  }, [currentProject?.id, (currentPlot as any)?.id]);

  // Load data preview, summary, and statistical analysis
  useEffect(() => {
    // If a new draft is bound with no fingerprint yet, clear stale caches immediately
    try {
      const curId = String((currentPlot as any)?.id || '');
      const hasFingerprint = Boolean((currentPlot as any)?.latestDataFingerprintId || localFingerprintId);
      if (editorMode === 'bound' && curId && !hasFingerprint) {
        setDataPreview([]);
        setDataSummary(null);
        setStatisticalAnalysis(null);
        setPlotStats(null);
        setFigureSummary(null);
        setPlotlyFigure(null);
      }
    } catch {}

    if (editorMode !== 'bound') { return; }
    // Drive by currentPlot.latestDataFingerprintId; if missing, fall back to store context getter
    const projId = String(currentProject?.id || (currentPlot as any)?.projectId || '');
    const pId = String((currentPlot as any)?.id || '');
    const storeFp = (() => {
      try { return (usePlotsStore.getState() as any).getLiveDataFingerprint?.(projId, pId) || null; } catch { return null; }
    })();
    const fingerprintId = (currentPlot as any)?.latestDataFingerprintId || storeFp || localFingerprintId;
    if (!fingerprintId || typeof window === 'undefined') return;
    console.log('Loading data for fingerprint:', fingerprintId);

    const consumeFingerprint = (fingerprint: any) => {
      if (!fingerprint) return;
      console.log('Found fingerprint data:', fingerprint);
      const dataToPreview = fingerprint.preview || fingerprint.data || fingerprint.actualData || [];
      if (Array.isArray(dataToPreview) && dataToPreview.length > 0) {
        setDataPreview(dataToPreview);
      }
      const columnTypes = fingerprint.columnTypes || {};
      const columnNames = Array.isArray(fingerprint.columns) ? fingerprint.columns : (dataToPreview[0] ? Object.keys(dataToPreview[0]) : []);
      const numericColumns = Object.keys(columnTypes).filter((k) => columnTypes[k] === 'numeric');
      const categoricalColumns = Object.keys(columnTypes).filter((k) => columnTypes[k] === 'categorical');
      setDataSummary({
        rows: fingerprint.rows || fingerprint.rowCount || fingerprint.row_count || dataToPreview.length,
        columns: columnNames.length,
        columnNames,
        numericColumns,
        categoricalColumns,
        missingValues: fingerprint.missing || {},
        statistics: fingerprint.statistics || {}
      });
      if (fingerprint.statistics) setStatisticalAnalysis(fingerprint.statistics);
    };

    // Try in-memory globals first
    const fpMem = (window as any).demoFingerprints?.[fingerprintId];
    if (fpMem) { consumeFingerprint(fpMem); }

    // Otherwise fetch via id
    const fetchByProject = async () => {
      try {
        const fileId = fingerprintId;
        if (!fileId) return;

        const fpResp = await apiRequest('GET', `/api/ai/v2/fingerprint/${fileId}`);
        if (!fpResp.ok) return;
        const json = await fpResp.json();
        if (json?.success && json?.fingerprint) {
          window.demoFingerprints = window.demoFingerprints || {};
          window.demoFingerprints[json.fingerprintId] = json.fingerprint;
          consumeFingerprint(json.fingerprint);

          // If no preview rows in fingerprint, fetch table rows for preview now
          if ((!Array.isArray(json.fingerprint.preview) || json.fingerprint.preview.length === 0) && fileId) {
            try {
              const resp = await apiRequest('GET', buildPreviewUrl(fileId));
              const table = await resp.json();
              if (Array.isArray(table) && table.length > 0) {
                setDataPreview(table);
              }
            } catch (e) {
              console.warn('Could not fetch file data for preview');
            }
          }
        }
      } catch {}
    };
    fetchByProject();
  }, [editorMode, localFingerprintId, currentProject?.id, (currentPlot as any)?.latestDataFingerprintId]);

  // Load artifacts for the currently open plot with graceful 404s and fallback to draft when present
  useEffect(() => {
    if (editorMode !== 'bound') {
      return;
    }
    const plotId = (currentPlot as any)?.id;
    const signal = { cancelled: false };
    if (!plotId) {
      setPlotStats(null);
      setFigureSummary(null);
      setPlotlyFigure(null);
      setUsingDraftArtifacts(false);
      return;
    }
    loadArtifactsForPlot(plotId, { signal });
    return () => {
      signal.cancelled = true;
    };
  }, [editorMode, (currentPlot as any)?.id, currentProject?.id]);

  // Step-2 defensive: if stats/figure are present but fingerprint id is missing or stale, pull dataset id from meta and set it
  useEffect(() => {
    (async () => {
      try {
        if (editorMode !== 'bound') return;
        const pid = String(currentProject?.id || (currentPlot as any)?.projectId || '');
        const sid = String((currentPlot as any)?.id || '');
        if (!pid || !sid) return;
        const hasStatsOrFig = Boolean(plotStats || plotlyFigure || figureSummary);
        // Only backfill if we have stats/figure but no fingerprint, or if fingerprint might be stale
        // Check if fingerprint exists - if not, try to fetch from metadata
        const existingFp = (currentPlot as any)?.latestDataFingerprintId || localFingerprintId || null;
        if (!hasStatsOrFig) return;
        // If we have stats/figure but no fingerprint, try to backfill from metadata
        if (!existingFp) {
          let meta: any = null;
          const tryBackoff = (url: string) => fetchWithBackoff(url, 5);
          meta = await tryBackoff(`/api/ai/v2/plots/${sid}/versions/latest/metadata.json`);
          if (!meta) meta = await tryBackoff(`/api/ai/v2/plots/${sid}/versions/latest/meta.json`);
          if (!meta) meta = await tryBackoff(`/api/ai/v2/drafts/${pid}/${sid}/meta.json`);
          const fid = String((meta as any)?.datasetFileId || (meta as any)?.dataFingerprintId || '') || null;
          if (fid) {
            if ((import.meta as any).env?.MODE !== 'production') console.info('datatab/meta_backfill', { projectId: pid, plotId: sid, fid });
            try { setLiveDataFingerprint(pid, sid, fid, { source: 'data-tab:meta' }); } catch {}
            try { setLocalFingerprintId(fid); } catch {}
            try { (usePlotsStore.getState() as any).setCurrentPlotArtifacts?.({ id: sid, projectId: pid, latestDataFingerprintId: fid } as any); } catch {}
          }
        }
      } catch {}
    })();
  }, [editorMode, currentProject?.id, (currentPlot as any)?.id, plotStats, plotlyFigure, figureSummary, (currentPlot as any)?.latestDataFingerprintId, localFingerprintId]);

  // Notify once when falling back to draft artifacts for the current plot
  useEffect(() => {
    try {
      const plotId = String((currentPlot as any)?.id || '');
      if (usingDraftArtifacts && plotId && draftToastForPlot !== plotId) {
        toast({
          title: 'Using draft artifacts',
          description: 'Loading stats/code from the draft while save completes. Latest version will appear shortly.',
        });
        setDraftToastForPlot(plotId);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usingDraftArtifacts, (currentPlot as any)?.id]);

  // Refetch artifacts after a save completes (new latest version)
  useEffect(() => {
    const refetchLatest = async (pid?: string) => {
      try {
        const plotId = String(pid || (currentPlot as any)?.id || '');
        if (!plotId) return;
        const [statsLatest, figureLatest, plotlyLatest] = await Promise.all([
          fetchArtifactJson(`/api/ai/v2/plots/${plotId}/versions/latest/stats.json`),
          fetchArtifactJson(`/api/ai/v2/plots/${plotId}/versions/latest/figure.json`),
          fetchArtifactJson(`/api/ai/v2/plots/${plotId}/versions/latest/plotly_figure.json`),
        ]);
        if (statsLatest) setPlotStats(normalizeStatsJson(statsLatest));
        if (figureLatest) setFigureSummary(figureLatest);
        if (plotlyLatest) setPlotlyFigure(plotlyLatest);
        setUsingDraftArtifacts(false);
      } catch {}
    };
    const onSave = (e: any) => {
      const pid = e?.detail?.plotId;
      refetchLatest(pid);
    };
    window.addEventListener('lumiplot-save-success', onSave as any);
    return () => window.removeEventListener('lumiplot-save-success', onSave as any);
  }, [(currentPlot as any)?.id]);

  // Listen for server-side readiness signal to refetch latest artifacts
  useEffect(() => {
    const pid = String((currentProject as any)?.id || '');
    const plotId = String((currentPlot as any)?.id || '');
    if (!pid || !plotId) return;
    // Ensure project subscription so server routes event correctly
    (async () => { try { await subscribeProject(pid, plotId); } catch {} })();
    const handler = (payload: PlotArtifactsReadyPayload) => {
      try {
        if (payload.projectId !== pid || payload.plotId !== plotId) return;
        // Refetch with short backoff in case of storage propagation delay
        (async () => {
          const v = payload.versionId;
          const key = `${plotId}:${v}`;
          const seen = seenVersionsRef.current;
          if (seen.has(key)) return;
          seen.add(key);
          
          // Extract fingerprint ID from payload or fetch from metadata
          let fingerprintId = payload.dataFingerprintId || null;
          if (!fingerprintId) {
            try {
              const metaLatest = await fetchWithBackoff(`/api/ai/v2/plots/${plotId}/versions/${v}/metadata.json`);
              if (metaLatest) {
                fingerprintId = String((metaLatest as any)?.datasetFileId || (metaLatest as any)?.dataFingerprintId || '') || null;
              }
            } catch {}
          }
          
          // Update fingerprint ID if we have one
          if (fingerprintId) {
            try {
              setLiveDataFingerprint(pid, plotId, fingerprintId, { source: 'plot_artifacts_ready' });
              setLocalFingerprintId(fingerprintId);
              try { (usePlotsStore.getState() as any).setCurrentPlotArtifacts?.({ id: plotId, projectId: pid, latestDataFingerprintId: fingerprintId } as any); } catch {}
            } catch {}
          }
          
          const [statsLatest, figureLatest, plotlyLatest] = await Promise.all([
            fetchWithBackoff(`/api/ai/v2/plots/${plotId}/versions/${v}/stats.json`),
            fetchWithBackoff(`/api/ai/v2/plots/${plotId}/versions/${v}/figure.json`),
            fetchWithBackoff(`/api/ai/v2/plots/${plotId}/versions/${v}/plotly_figure.json`),
          ]);
          if (statsLatest) setPlotStats(normalizeStatsJson(statsLatest));
          if (figureLatest) setFigureSummary(figureLatest);
          if (plotlyLatest) setPlotlyFigure(plotlyLatest);
          setUsingDraftArtifacts(false);
        })();
      } catch {}
    };
    socket.on('plot_artifacts_ready', handler);
    return () => { try { socket.off('plot_artifacts_ready', handler); } catch {} };
  }, [currentProject?.id, (currentPlot as any)?.id]);

  // When a plot is generated, extract statistical test results from annotations (e.g., p-values)
  useEffect(() => {
    try {
      const annotations = (currentPlot as any)?.plotlyJson?.layout?.annotations;
      if (!Array.isArray(annotations) || annotations.length === 0) return;

      const testResults: Array<{ text: string; pValue?: number; effectSize?: number }> = [];
      for (const a of annotations) {
        const text: string = a?.text || '';
        if (!text) continue;
        const pMatch = text.match(/p[-\s]?value\s*[:=]\s*([0-9]*\.?[0-9]+)/i);
        const etaMatch = text.match(/effect\s*size.*?([0-9]*\.?[0-9]+)/i);
        if (pMatch || etaMatch) {
          const pVal = pMatch ? Number(pMatch[1]) : undefined;
          const eta = etaMatch ? Number(etaMatch[1]) : undefined;
          testResults.push({ text, pValue: isNaN(pVal as number) ? undefined : pVal, effectSize: isNaN(eta as number) ? undefined : eta });
        }
      }

      if (testResults.length > 0) {
        setStatisticalAnalysis((prev: any) => ({ ...(prev || {}), testResults }));
      }
    } catch {}
  }, [(currentPlot as any)?.plotlyJson?.layout?.annotations]);

  const handleFileUploaded = async (fileData: any) => {
    console.log('Data uploaded in Data tab:', fileData);
    
    // Check if fingerprint was already created during upload (demo mode)
    if (fileData.fingerprintId && fileData.fingerprint) {
      // Store in global for demo access
      if (typeof window !== 'undefined') {
        window.demoFingerprints = window.demoFingerprints || {};
        // Persist fingerprint and the parsed table if available
        window.demoFingerprints[fileData.fingerprintId] = fileData.fingerprint;
        setLocalFingerprintId(fileData.fingerprintId);
      }

      // Immediately reflect in UI using returned fingerprint
      try {
        const fp = fileData.fingerprint;
        let dataToPreview = fp.preview || fp.data || [];
        // If server didn't include preview rows, fetch them now
        if ((!Array.isArray(dataToPreview) || dataToPreview.length === 0) && fileData.id) {
          try {
            const resp = await apiRequest('GET', buildPreviewUrl(fileData.id));
            const table = await resp.json();
            if (Array.isArray(table) && table.length > 0) {
              dataToPreview = table;
            }
          } catch (e) {
            console.warn('Could not fetch file data for preview');
          }
        }
        if (dataToPreview.length > 0) setDataPreview(dataToPreview);
        const columnTypes = fp.columnTypes || {};
        const columnNames = fp.columns || (dataToPreview[0] ? Object.keys(dataToPreview[0]) : []);
        const numericColumns = Object.keys(columnTypes).filter((k) => columnTypes[k] === 'numeric');
        const categoricalColumns = Object.keys(columnTypes).filter((k) => columnTypes[k] === 'categorical');
        setDataSummary({
          rows: fp.rows || fileData.rowCount || dataToPreview.length,
          columns: columnNames.length,
          columnNames,
          numericColumns,
          categoricalColumns,
          missingValues: fp.missing || {},
          statistics: fp.statistics || {}
        });
        if (fp.statistics) setStatisticalAnalysis(fp.statistics);

        // Broadcast to Lumi to show analysis message
        try {
          console.log('🚀 Dispatching lumi:dataAnalyzed event (upload response case):', {
            filename: fileData.filename,
            rows: fp.rows || dataToPreview.length,
            columns: columnNames,
            suggestions: fp.suggestions?.plotTypes || [],
            patterns: fp.patterns || {},
            fingerprintId: fileData.fingerprintId,
            fileId: fileData.id
          });
          
          window.dispatchEvent(new CustomEvent('lumi:dataAnalyzed', {
            detail: {
              filename: fileData.filename,
              rows: fp.rows || dataToPreview.length,
              columns: columnNames,
              suggestions: fp.suggestions?.plotTypes || [],
              patterns: fp.patterns || {},
              fingerprintId: fileData.fingerprintId,
              fileId: fileData.id,
              table: null
            }
          }));
          
          console.log('✅ lumi:dataAnalyzed event dispatched successfully (upload response case)');
        } catch (error) {
          console.error('❌ Failed to dispatch lumi:dataAnalyzed event (upload response case):', error);
        }
      } catch {}
    } else if (currentProject) {
      // If no fingerprint, try to fetch an existing one or analyze this upload
      try {
        // First try to load fingerprint by fileId
        const fpResp = await apiRequest('GET', `/api/ai/v2/fingerprint/${fileData.id}`);
        if (fpResp.ok) {
          const fpJson = await fpResp.json();
          if (fpJson.success && fpJson.fingerprintId) {
            setLocalFingerprintId(fpJson.fingerprintId);
            window.demoFingerprints = window.demoFingerprints || {};
            window.demoFingerprints[fpJson.fingerprintId] = fpJson.fingerprint;
            // Try to fetch table for preview
            try {
              const resp = await apiRequest('GET', buildPreviewUrl(fileData.id));
              const table = await resp.json();
              if (Array.isArray(table) && table.length > 0) {
                setDataPreview(table);
              }
            } catch {}
            // Broadcast minimal analysis message
            try {
              window.dispatchEvent(new CustomEvent('lumi:dataAnalyzed', {
                detail: {
                  filename: fileData.filename,
                  rows: fpJson.fingerprint?.rows,
                  columns: fpJson.fingerprint?.columns || [],
                  suggestions: fpJson.fingerprint?.suggestions?.plotTypes || [],
                  patterns: fpJson.fingerprint?.patterns || {},
                  fingerprintId: fpJson.fingerprintId,
                  fileId: fileData.id
                }
              }));
            } catch {}
          }
        } else {
          // No fingerprint found, but we can still fetch the data for preview
          try {
            const resp = await apiRequest('GET', buildPreviewUrl(fileData.id));
            const table = await resp.json();
            if (Array.isArray(table) && table.length > 0) {
              setDataPreview(table);
              // Update data summary with real data
              const columnNames = Object.keys(table[0] || {});
              setDataSummary({
                rows: table.length,
                columns: columnNames.length,
                columnNames,
                numericColumns: [],
                categoricalColumns: [],
                missingValues: {},
                statistics: {}
              });
            }
          } catch (e) {
            console.warn('Could not fetch file data for preview:', e);
          }
        }
      } catch (error) {
        console.error('Error analyzing data:', error);
      }
    }

    setShowUpload(false);
    if (onDataReplaced) {
      onDataReplaced();
    }
  };

  // Check if we have data either from props, global, or project files
  const [projectFiles, setProjectFiles] = useState<any[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);

  // Fetch project files to determine if data exists
  useEffect(() => {
    console.log('🔍 DataTab: currentProject changed:', currentProject);
    if (currentProject?.id) {
      console.log('🔍 DataTab: Fetching files for project:', currentProject.id);
      setIsLoadingFiles(true);
      apiRequest('GET', `/api/projects/${currentProject.id}/files`)
        .then(res => {
          console.log('🔍 DataTab: API response status:', res.status);
          return res.json();
        })
        .then(files => {
          console.log('🔍 DataTab: Received files:', files);
          setProjectFiles(files || []);
          // Log the actual files received, not the stale state
          console.log('🔍 DataTab: Files set to state:', files);
        })
        .catch(err => {
          console.error('🔍 DataTab: Failed to fetch project files:', err);
          setProjectFiles([]);
        })
        .finally(() => {
          setIsLoadingFiles(false);
        });
    } else {
      console.log('🔍 DataTab: No currentProject or project ID');
    }
  }, [currentProject?.id]);

  const hasFingerprintContext = Boolean((currentPlot as any)?.latestDataFingerprintId || localFingerprintId);
  const hasPreviewTable = Array.isArray(dataPreview) && dataPreview.length > 0;
  // Consider presence of plot artifacts as evidence of hydration, so Data tab doesn't look empty
  const hasArtifactStats = !!(plotStats && Object.keys(plotStats || {}).length > 0);
  const hasArtifactFigure = !!(figureSummary && Object.keys(figureSummary || {}).length > 0);
  const hasAnyArtifacts = hasArtifactStats || hasArtifactFigure || !!(currentPlot as any)?.plotlyJson;
  const hasData = hasFingerprintContext || hasPreviewTable || hasAnyArtifacts;
  
  console.log('🔍 DataTab: Render state:', {
    latestDataFingerprintId: (currentPlot as any)?.latestDataFingerprintId,
    projectFilesLength: projectFiles.length,
    hasData,
    currentProject: currentProject?.id
  });

  // Function to load/analyze data from a specific file via the unified analyze pipeline
  const loadFileData = async (fileId: string) => {
    const projId = String(currentProject?.id || (currentPlot as any)?.projectId || '');
    const pId = String((currentPlot as any)?.id || '');
    if (!projId || !pId || !fileId) return;
    const analyzeKey = `${projId}:${pId}:${fileId}`;
    if (inflightAnalyzeByFileRef.current[analyzeKey]) {
      try { await inflightAnalyzeByFileRef.current[analyzeKey]; } catch {}
      return;
    }
    inflightAnalyzeByFileRef.current[analyzeKey] = (async () => {
      try {
        // Obtain or create fingerprint for this file
        const fpResp = await apiRequest('GET', `/api/ai/v2/fingerprint/${fileId}`);
        if (!fpResp || !fpResp.ok) {
          // Fallback: fetch preview to show something, but prefer analysis pipeline
          try {
            const tableResp = await apiRequest('GET', `/api/files/${fileId}/preview?rows=100`);
            const table = await tableResp.json();
            if (Array.isArray(table) && table.length > 0) setDataPreview(table);
          } catch {}
          return;
        }
        const json = await fpResp.json();
        const fingerprintId = json?.fingerprintId || json?.fingerprint_id || fileId;
        const fingerprint = json?.fingerprint || null;
        if (typeof window !== 'undefined') {
          (window as any).demoFingerprints = (window as any).demoFingerprints || {};
          if (fingerprint) (window as any).demoFingerprints[fingerprintId] = fingerprint;
        }
        // Reflect context for this editor session
        setLocalFingerprintId(fingerprintId);
        try { setLiveDataFingerprint?.(projId, pId, fingerprintId, { fileId }); } catch {}
        try {
          window.dispatchEvent(new CustomEvent('lumi:dataAnalyzed', {
            detail: { projectId: projId, plotId: pId, fingerprintId, fileId, fingerprint }
          }));
        } catch {}
      } catch (error) {
        console.error('Failed to analyze/load file data:', error);
      }
    })();
    try { await inflightAnalyzeByFileRef.current[analyzeKey]; } catch {}
  };

  // Mark hydration + once-per-fingerprint debug when summary is ready
  useEffect(() => {
    try {
      const projId = String(currentProject?.id || (currentPlot as any)?.projectId || '');
      const pId = String((currentPlot as any)?.id || '');
      const fpId = (currentPlot as any)?.latestDataFingerprintId || localFingerprintId || '';
      if (!projId || !pId || !fpId) return;
      if (dataSummary) {
        const key = `${projId}:${pId}:${fpId}`;
        if (!hydratedLoggedRef.current.has(key)) {
          hydratedLoggedRef.current.add(key);
          try { console.debug('DataTab hydrated', { projectId: projId, plotId: pId, fingerprintId: fpId }); } catch {}
        }
        try {
          if (!isFingerprintHydrated?.(projId, pId, fpId)) {
            markFingerprintHydrated?.(projId, pId, fpId);
          }
        } catch {}
      }
    } catch {}
  }, [dataSummary, currentProject?.id, (currentPlot as any)?.id, (currentPlot as any)?.latestDataFingerprintId, localFingerprintId]);

  useEffect(() => {
    if (activeTab !== 'statistics') return;
    const plotId = String((currentPlot as any)?.id || '');
    if (!plotId) return;
    let cancelled = false;
    (async () => {
      try {
        setIsLoadingPlotStats(true);
        const statsLatest = await fetchArtifactJson(`/api/ai/v2/plots/${plotId}/versions/latest/stats.json`);
        if (!cancelled && statsLatest) setPlotStats(normalizeStatsJson(statsLatest));
      } finally {
        if (!cancelled) setIsLoadingPlotStats(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, (currentPlot as any)?.id]);

  if (editorMode !== 'bound') {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-3">
          <Database className="h-16 w-16 text-muted-foreground mx-auto" />
          <h3 className="text-lg font-medium">No data yet</h3>
          <p className="text-sm text-muted-foreground">Start a plot from chat to bind this editor to a draft and view your data here.</p>
        </div>
      </div>
    );
  }

  if (!hasData || isSelectedPending) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-4">
          <Database className="h-16 w-16 text-muted-foreground mx-auto" />
          <h3 className="text-lg font-medium">{isSelectedPending ? 'Preparing your dataset…' : 'No data loaded'}</h3>
          <p className="text-sm text-muted-foreground">{isSelectedPending ? 'Were analyzing your file. This usually takes a few seconds.' : (isLoadingFiles ? 'Fetching your project files…' : 'Upload a CSV to get instant previews and summaries.')}</p>
          <Button onClick={() => setShowUpload(true)} data-lumi="btn-upload">
            <Upload className="mr-2 h-4 w-4" />
            Upload Data
          </Button>
        </div>
        
        {/* Show project files if no preview data */}
        {projectFiles.length > 0 && (
          <div className="mt-6 p-4 border rounded-lg bg-muted/20">
            <h4 className="font-medium mb-2">Project Files ({projectFiles.length})</h4>
            <div className="space-y-2">
              {projectFiles.map((file) => (
                <div key={file.id} className="flex items-center justify-between p-2 bg-white rounded border">
                  <div className="flex items-center gap-2">
                    <Database className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{file.filename}</span>
                    <Badge variant="secondary">{file.rowCount} rows × {file.columnCount} cols</Badge>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadFileData(file.id)}
                  >
                    Load data
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {showUpload && currentProject && (
          <FileUpload
            projectId={currentProject.id}
            onClose={() => setShowUpload(false)}
            onFileUploaded={handleFileUploaded}
          />
        )}
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <Tabs defaultValue="data" className="flex-1 flex flex-col" onValueChange={(v: any) => setActiveTab(v)}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="data">Data Preview</TabsTrigger>
          <TabsTrigger value="summary">Summary</TabsTrigger>
          <TabsTrigger value="statistics">Statistical Analysis</TabsTrigger>
        </TabsList>

        {/* Data Preview Tab */}
        <TabsContent value="data" className="flex-1 overflow-hidden">
          <Card className="h-full flex flex-col bg-white border border-border">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Database className="h-4 w-4" />
                  Uploaded Data
                  {usingDraftArtifacts && (
                    <Badge variant="secondary" className="ml-2" title="Showing draft artifacts until save completes">Draft artifacts</Badge>
                  )}
                </span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowUpload(true)}
                        title="Replace Data"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Replace Data</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 overflow-hidden">
              <div className="h-full min-h-0 overflow-auto">
                {Array.isArray(dataPreview) && dataPreview.length > 0 ? (
                  <div className="overflow-x-auto border rounded-md w-full">
                    {!featureVirtual ? (
                      <table className="min-w-max text-sm data-table">
                        <thead className="sticky top-0 z-10">
                          <tr className="border-b bg-white">
                            <th className="px-3 py-2 text-left font-medium text-muted-foreground sticky left-0 bg-white whitespace-nowrap">#</th>
                            {Object.keys(dataPreview[0] || {}).map((col) => (
                              <th key={col as any} className="px-3 py-2 text-left font-medium text-muted-foreground whitespace-nowrap">
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {dataPreview.slice(0, 200).map((row, idx) => (
                            <tr key={idx} className="border-b hover:bg-muted/30 transition-colors">
                              <td className="px-3 py-1 text-muted-foreground font-mono text-xs sticky left-0 bg-white whitespace-nowrap">{idx + 1}</td>
                              {Object.entries(row).map(([_key, val]: any, colIdx) => {
                                const isNumeric = typeof val === 'number' || (!isNaN(Number(val)) && val !== '' && val !== null);
                                return (
                                  <td key={colIdx} className={`px-3 py-1 whitespace-nowrap ${isNumeric ? 'text-right font-mono numeric' : ''}`}>
                                    {val !== null && val !== undefined ? String(val) : '-'}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="min-w-max">
                        <table className="min-w-max text-sm data-table w-full">
                          <thead className="sticky top-0 z-10">
                            <tr className="border-b bg-white">
                              <th className="px-3 py-2 text-left font-medium text-muted-foreground sticky left-0 bg-white whitespace-nowrap">#</th>
                              {Object.keys(dataPreview[0] || {}).map((col) => (
                                <th key={col as any} className="px-3 py-2 text-left font-medium text-muted-foreground whitespace-nowrap">
                                  {col}
                                </th>
                              ))}
                            </tr>
                          </thead>
                        </table>
                        <Virtuoso
                          style={{ height: '50vh' }}
                          totalCount={dataPreview.length}
                          itemContent={(index) => {
                            const row = dataPreview[index];
                            return (
                              <tr className="border-b hover:bg-muted/30 transition-colors">
                                <td className="px-3 py-1 text-muted-foreground font-mono text-xs sticky left-0 bg-white whitespace-nowrap">{index + 1}</td>
                                {Object.entries(row || {}).map(([_key, val]: any, colIdx) => {
                                  const isNumeric = typeof val === 'number' || (!isNaN(Number(val)) && val !== '' && val !== null);
                                  return (
                                    <td key={colIdx} className={`px-3 py-1 whitespace-nowrap ${isNumeric ? 'text-right font-mono numeric' : ''}`}>
                                      {val !== null && val !== undefined ? String(val) : '-'}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          }}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Database className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No preview yet</p>
                    
                    {/* Show project files if available */}
                    {projectFiles.length > 0 && (
                      <div className="mt-4 p-4 border rounded-lg bg-muted/20">
                        <h4 className="font-medium mb-2">Project Files ({projectFiles.length})</h4>
                        <div className="space-y-2">
                          {projectFiles.map((file) => (
                            <div key={file.id} className="flex items-center justify-between p-2 bg-white rounded border">
                              <div className="flex items-center gap-2">
                                <Database className="h-4 w-4 text-muted-foreground" />
                                <span className="font-medium">{file.filename}</span>
                                <Badge variant="secondary">{file.rowCount} rows × {file.columnCount} cols</Badge>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => loadFileData(file.id)}
                              >
                                Load Data
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    
                  </div>
                )}
              </div>
              {featureVirtual && dataPreview.length > 100 && (
                <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={virtualWindow.start === 0}
                    onClick={() => setVirtualWindow(v => ({ start: Math.max(0, v.start - 100), end: Math.max(100, v.end - 100) }))}
                  >
                    Prev 100
                  </Button>
                  <div>
                    Page {Math.floor(virtualWindow.start / 100) + 1} / {Math.ceil(dataPreview.length / 100)}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={virtualWindow.end >= dataPreview.length}
                    onClick={() => setVirtualWindow(v => ({ start: Math.min(Math.max(0, dataPreview.length - 100), v.start + 100), end: Math.min(dataPreview.length, v.end + 100) }))}
                  >
                    Next 100
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Summary Tab */}
        <TabsContent value="summary" className="flex-1 overflow-hidden">
          <Card className="h-full">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  Data Summary
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dataSummary ? (
                <div className="space-y-4">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                    <div>
                      <span className="text-muted-foreground text-sm">Total Rows</span>
                      <p className="text-2xl font-bold">{(dataSummary.rows || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground text-sm">Total Columns</span>
                      <p className="text-2xl font-bold">{dataSummary.columns || 0}</p>
                    </div>
                  </div>
                  
                  {/* Column Details */}
                  <div className="space-y-3">
                    <h4 className="font-medium text-sm">Column Information</h4>
                    
                    {dataSummary.columnNames && dataSummary.columnNames.length > 0 && (
                      <div className="space-y-2">
                        <div className="text-sm text-muted-foreground">All Columns ({dataSummary.columnNames.length})</div>
                        <div className="flex flex-wrap gap-2">
                          {dataSummary.columnNames.map((col: string) => {
                            const isNumeric = dataSummary.numericColumns?.includes(col);
                            const isCategorical = dataSummary.categoricalColumns?.includes(col);
                            return (
                              <div key={col} className="flex items-center gap-1">
                                <span className={`px-2 py-1 rounded text-xs font-mono ${
                                  isNumeric ? 'bg-blue-500/10 text-blue-600' : 
                                  isCategorical ? 'bg-green-500/10 text-green-600' : 
                                  'bg-secondary'
                                }`}>
                                  {col}
                                </span>
                                {isNumeric && <span className="text-xs text-muted-foreground">(numeric)</span>}
                                {isCategorical && <span className="text-xs text-muted-foreground">(categorical)</span>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Data Types Summary */}
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      {dataSummary.numericColumns?.length > 0 && (
                        <div className="p-3 bg-blue-500/5 rounded-lg">
                          <div className="flex items-center gap-2 text-blue-600">
                            <TrendingUp className="h-4 w-4" />
                            <span className="text-sm font-medium">Numeric Columns</span>
                          </div>
                          <p className="text-2xl font-bold mt-1">{dataSummary.numericColumns.length}</p>
                        </div>
                      )}
                      
                      {dataSummary.categoricalColumns?.length > 0 && (
                        <div className="p-3 bg-green-500/5 rounded-lg">
                          <div className="flex items-center gap-2 text-green-600">
                            <Database className="h-4 w-4" />
                            <span className="text-sm font-medium">Categorical Columns</span>
                          </div>
                          <p className="text-2xl font-bold mt-1">{dataSummary.categoricalColumns.length}</p>
                        </div>
                      )}
                    </div>

                    {/* Missing Values */}
                    {dataSummary.missingValues && Object.keys(dataSummary.missingValues).length > 0 && (
                      <div className="mt-4">
                        <div className="flex items-center gap-2 text-sm font-medium mb-2">
                          <AlertCircle className="h-4 w-4 text-orange-500" />
                          Missing Values
                        </div>
                        <div className="space-y-1">
                          {Object.entries(dataSummary.missingValues).map(([col, count]: [string, any]) => (
                            <div key={col} className="flex justify-between text-sm">
                              <span className="text-muted-foreground">{col}</span>
                              <span className="font-mono">{count} missing</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {!dataSummary.missingValues && (
                      <div className="mt-4 text-sm text-muted-foreground">No missing values detected.</div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Info className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No summary yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Statistical Analysis Tab */}
        <TabsContent value="statistics" className="flex-1 overflow-hidden">
          <Card className="h-full">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Statistical Analysis
                </span>
                {((plotStats && Object.keys(plotStats || {}).length > 0) || (figureSummary && Object.keys(figureSummary || {}).length > 0) || (statisticalAnalysis && Object.keys(statisticalAnalysis || {}).length > 0)) && (
                  <button
                    onClick={async () => {
                      const text = formatStatsAsText(plotStats, figureSummary);
                      await copyToClipboard(text);
                      setCopiedStats(true);
                      toast({
                        title: "Copied!",
                        description: "Statistical analysis copied to clipboard",
                        duration: 2000,
                      });
                      if (copiedStatsTimeoutRef.current) window.clearTimeout(copiedStatsTimeoutRef.current);
                      // @ts-ignore setTimeout returns number in browsers
                      copiedStatsTimeoutRef.current = window.setTimeout(() => setCopiedStats(false), 2000);
                    }}
                    className="h-7 w-7 p-0 opacity-60 hover:opacity-100 transition-opacity flex-shrink-0 flex items-center justify-center rounded hover:bg-accent"
                    aria-label="Copy statistical analysis to clipboard"
                  >
                    {copiedStats ? (
                      <Check className="h-4 w-4 text-emerald-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                    <span className="sr-only" aria-live="polite">{copiedStats ? 'Copied to clipboard' : ''}</span>
                  </button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-full">
                {((plotStats && Object.keys(plotStats || {}).length > 0) || (figureSummary && Object.keys(figureSummary || {}).length > 0) || (statisticalAnalysis && Object.keys(statisticalAnalysis || {}).length > 0)) ? (
                  <div className="space-y-4">
                    {/* Stats from last plot run (stats.json) - Normalized display */}
                    <div className="p-4 bg-white border rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm">Methods & Results (from last run)</h4>
                        {isLoadingPlotStats && <span className="text-xs text-muted-foreground">Loading…</span>}
                      </div>
                      {plotStats || figureSummary ? (
                        <div className="space-y-4 text-sm">
                          {/* Normalized stats display */}
                          {(() => {
                            const normalizedRows = normalizeStats(plotStats as StatsJson);
                            if (normalizedRows.length === 0) return null;
                            
                            // Group by section
                            const rowsBySection = normalizedRows.reduce((acc, row) => {
                              if (!acc[row.section]) acc[row.section] = [];
                              acc[row.section].push(row);
                              return acc;
                            }, {} as Record<string, NormalizedStatRow[]>);
                            
                            const sectionLabels: Record<string, string> = {
                              group_stats: "Group Summaries",
                              omnibus: "Omnibus Tests",
                              pairwise: "Pairwise Tests",
                              regression: "Regression",
                              descriptive: "Descriptive",
                            };
                            
                            return (
                              <div className="space-y-4">
                                {(["group_stats", "omnibus", "pairwise", "regression", "descriptive"] as const).map((section) => {
                                  const rows = rowsBySection[section] || [];
                                  if (rows.length === 0) return null;
                                  
                                  return (
                                    <div key={section} className="space-y-2">
                                      <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                        {sectionLabels[section]}
                                      </h5>
                                      <div className="space-y-1">
                                        {rows.map((row, idx) => (
                                          <div key={idx} className="p-2 bg-muted/30 rounded text-xs">
                                            <div className="font-medium">{row.label}</div>
                                            {row.value != null && (
                                              <div className="text-muted-foreground font-mono mt-1">
                                                {typeof row.value === 'number' 
                                                  ? (row.value < 1e-3 ? row.value.toExponential(2) : row.value.toPrecision(3))
                                                  : String(row.value)}
                                              </div>
                                            )}
                                            {/* Show key details */}
                                            {Object.keys(row.details).length > 0 && (
                                              <details className="mt-2">
                                                <summary className="text-xs text-muted-foreground cursor-pointer">Details</summary>
                                                <pre className="mt-1 text-xs overflow-x-auto">
                                                  {JSON.stringify(row.details, null, 2)}
                                                </pre>
                                              </details>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                          
                          {/* Legacy display (keep for backward compatibility) - HIDDEN */}
                          {false && (
                          <div className="space-y-3 text-sm border-t pt-3 mt-3">
                            <div className="text-xs text-muted-foreground mb-2">Legacy Display (backward compatibility)</div>
                          {/* N per group (single-location display) */}
                          {(() => {
                            const countsLegacy = (plotStats as any)?.n_per_group || (plotStats as any)?.group_counts || (plotStats as any)?.groupCounts;
                            const countsX = (plotStats as any)?.n_by_x;
                            const countsHue = (plotStats as any)?.n_by_hue;
                            // Collect all n_by_* patterns (e.g., n_by_pathway, n_by_category, etc.)
                            const nByPatterns: Array<{ key: string; value: any; label: string }> = [];
                            if (plotStats && typeof plotStats === 'object') {
                              for (const [k, v] of Object.entries(plotStats)) {
                                if (k.startsWith('n_by_') && k !== 'n_by_x' && k !== 'n_by_hue' && v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length > 0) {
                                  const fieldName = k.replace('n_by_', '').replace(/_/g, ' ');
                                  nByPatterns.push({ key: k, value: v, label: `n by ${fieldName}` });
                                }
                              }
                            }
                            const groupOrder = (plotStats as any)?.species_order || (plotStats as any)?.group_order || (plotStats as any)?.category_order;
                            const ciMethod = (plotStats as any)?.ci_method || (plotStats as any)?.ci?.method;
                            const labels = (figureSummary as any)?.group_labels || [];
                            const hasAny = (obj: any) => obj && typeof obj === 'object' && Object.keys(obj).length > 0;
                            if (hasAny(countsLegacy) || hasAny(countsX) || hasAny(countsHue) || nByPatterns.length > 0) {
                              return (
                                <div className="space-y-2">
                                  {hasAny(countsLegacy) && (
                                    <div className="flex flex-wrap gap-2">
                                      {Object.entries(countsLegacy).map(([g, n]: any) => (
                                        <Chip key={`legacy_${String(g)}`}>{String(g)} (n={Number(n) || 0})</Chip>
                                      ))}
                                    </div>
                                  )}
                                  {hasAny(countsX) && (
                                    <div>
                                      <div className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">Counts by x</div>
                                      <div className="flex flex-wrap gap-2">
                                        {Object.entries(countsX).map(([g, n]: any) => (
                                          <Chip key={`x_${String(g)}`}>{String(g)} (n={Number(n) || 0})</Chip>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {hasAny(countsHue) && (
                                    <div>
                                      <div className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">Counts by series</div>
                                      <div className="flex flex-wrap gap-2">
                                        {Object.entries(countsHue).map(([g, n]: any) => (
                                          <Chip key={`hue_${String(g)}`}>{String(g)} (n={Number(n) || 0})</Chip>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {nByPatterns.map(({ key, value, label }) => (
                                    <div key={key}>
                                      <div className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">{label}</div>
                                      <div className="flex flex-wrap gap-2">
                                        {Object.entries(value).map(([g, n]: any) => (
                                          <Chip key={`${key}_${String(g)}`}>{String(g)} (n={Number(n) || 0})</Chip>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                  {Array.isArray(groupOrder) && groupOrder.length > 0 && (
                                    <div>
                                      <div className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">Group order</div>
                                      <div className="flex flex-wrap gap-2">
                                        {groupOrder.map((g: any) => (
                                          <Chip key={`order_${String(g)}`}>{String(g)}</Chip>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {ciMethod && (
                                    <div className="text-[11px] text-muted-foreground">CI method: {String(ciMethod)}</div>
                                  )}
                                </div>
                              );
                            } else if (Array.isArray(labels) && labels.length > 0 && countsLegacy && Object.keys(countsLegacy).length === 0) {
                              return (
                                <div className="flex flex-wrap gap-2">
                                  {labels.map((g: any) => (
                                    <Chip key={String(g)}>{String(g)}</Chip>
                                  ))}
                                </div>
                              );
                            }
                            return null;
                          })()}

                          {/* Stats display quick lines from stats.json (generic) */}
                          {(() => {
                            try {
                              const sd: any = (plotStats as any)?.stats_display || {};
                              const lines: Array<{ key: string; text: string }> = [];
                              const hasStructuredPairs = Array.isArray((plotStats as any)?.pairwise) && (plotStats as any)?.pairwise.length > 0;
                              if (typeof sd.n_line === 'string' && sd.n_line.trim().length > 0) lines.push({ key: 'n', text: sd.n_line.trim() });
                              if (typeof sd.overall_line === 'string' && sd.overall_line.trim().length > 0) lines.push({ key: 'overall', text: sd.overall_line.trim() });
                              if (!hasStructuredPairs && Array.isArray(sd.pairwise_lines)) {
                                for (let i = 0; i < sd.pairwise_lines.length; i++) {
                                  const t = String(sd.pairwise_lines[i] || '').trim();
                                  if (t.length > 0) lines.push({ key: `pw_${i}`, text: t });
                                }
                              }
                              const insightRaw = typeof sd.insight === 'string' ? sd.insight.trim() : '';
                              const insightLooksValid = /p\s*=|ci|r\b|eta|delta|slope|r\(|\d/.test(insightRaw);
                              const hasInsight = Boolean(insightRaw && insightLooksValid);
                              if (lines.length === 0 && !hasInsight) return null;
                              return (
                                <div className="space-y-1">
                                  {lines.map((ln) => (
                                    <div key={ln.key} className="font-mono text-xs">{ln.text}</div>
                                  ))}
                                  {hasInsight && (
                                    <div className="text-xs italic text-muted-foreground">{insightRaw}</div>
                                  )}
                                </div>
                              );
                            } catch { return null; }
                          })()}

                          {/* Overall and overall hue differences (hide if no data) */}
                          {(() => {
                            // Helper to parse F-statistic from string formats like "F2,177= 3.081" or "F=3.081" or "F2,177=3.08"
                            const parseFStat = (val: any): number | null => {
                              if (val == null) return null;
                              if (typeof val === 'number' && isFinite(val)) return val;
                              if (typeof val === 'string') {
                                // Try to extract numeric value from formats like "F2,177= 3.081" or "F=3.081" or "F2,177=3.08"
                                // Match patterns: F2,177=3.08, F2,177= 3.08, F=3.08, F = 3.08
                                const match = val.match(/F\d*,\d*\s*=\s*([\d.]+)|F\s*=\s*([\d.]+)/i);
                                if (match) {
                                  const num = parseFloat(match[1] || match[2]);
                                  if (isFinite(num)) return num;
                                }
                                // Try direct parse
                                const num = parseFloat(val);
                                if (isFinite(num)) return num;
                              }
                              return null;
                            };

                            // Helper to parse p-value from string formats
                            const parsePValue = (val: any): number | null => {
                              if (val == null) return null;
                              if (typeof val === 'number' && isFinite(val)) return val;
                              if (typeof val === 'string') {
                                // Try to extract numeric value from formats like "p 0.0484" or "p=0.0484"
                                const match = val.match(/p\s*[=:]?\s*([\d.eE+-]+)/i);
                                if (match) {
                                  const num = parseFloat(match[1]);
                                  if (isFinite(num)) return num;
                                }
                                // Try direct parse
                                const num = parseFloat(val);
                                if (isFinite(num)) return num;
                              }
                              return null;
                            };

                            // Prefer structured omnibus from stats.json; support legacy shapes
                            // Check anova first since it's a common direct structure
                            let overall: any = null;
                            try {
                              const s: any = plotStats as any;
                              
                              // Check anova structure first (common direct format)
                              if (s?.anova && typeof s.anova === 'object') {
                                let anova = null;
                                
                                // First check for nested "overall" structure (e.g., {anova: {overall: {F: "3.081", p: "0.048"}}})
                                if ('overall' in s.anova && s.anova.overall && typeof s.anova.overall === 'object') {
                                  anova = s.anova.overall;
                                }
                                // Then check if it's a direct object with F and p_value (e.g., {F: 3.08, p_value: 0.048})
                                else if ('F' in s.anova || 'p_value' in s.anova || 'p' in s.anova || 'statistic' in s.anova || 'stat' in s.anova) {
                                  anova = s.anova;
                                } else {
                                  // Try nested format (e.g., {someKey: {F: 3.08, p_value: 0.048}})
                                  const anovaKey = Object.keys(s.anova)[0];
                                  if (anovaKey && s.anova[anovaKey] && typeof s.anova[anovaKey] === 'object') {
                                    anova = s.anova[anovaKey];
                                  }
                                }
                                
                                if (anova) {
                                  // Extract F-statistic - check multiple possible field names
                                  // Use explicit checks to handle 0 values correctly
                                  let fStat = null;
                                  if ('F' in anova) {
                                    const fVal = anova.F;
                                    if (fVal != null && fVal !== '' && (typeof fVal === 'number' || typeof fVal === 'string')) {
                                      fStat = fVal;
                                    }
                                  }
                                  if (fStat == null && 'statistic' in anova) {
                                    const sVal = anova.statistic;
                                    if (sVal != null && sVal !== '' && (typeof sVal === 'number' || typeof sVal === 'string')) {
                                      fStat = sVal;
                                    }
                                  }
                                  if (fStat == null && 'stat' in anova) {
                                    const sVal = anova.stat;
                                    if (sVal != null && sVal !== '' && (typeof sVal === 'number' || typeof sVal === 'string')) {
                                      fStat = sVal;
                                    }
                                  }
                                  
                                  // Extract p-value - check multiple possible field names
                                  let pVal = null;
                                  if ('p_value' in anova) {
                                    const pValCheck = anova.p_value;
                                    if (pValCheck != null && pValCheck !== '' && (typeof pValCheck === 'number' || typeof pValCheck === 'string')) {
                                      pVal = pValCheck;
                                    }
                                  }
                                  if (pVal == null && 'p' in anova) {
                                    const pValCheck = anova.p;
                                    if (pValCheck != null && pValCheck !== '' && (typeof pValCheck === 'number' || typeof pValCheck === 'string')) {
                                      pVal = pValCheck;
                                    }
                                  }
                                  if (pVal == null && 'pval' in anova) {
                                    const pValCheck = anova.pval;
                                    if (pValCheck != null && pValCheck !== '' && (typeof pValCheck === 'number' || typeof pValCheck === 'string')) {
                                      pVal = pValCheck;
                                    }
                                  }
                                  
                                  // Parse if values are strings (but don't overwrite valid numbers)
                                  if (fStat != null) {
                                    if (typeof fStat === 'string') {
                                      const parsedStat = parseFStat(fStat);
                                      if (parsedStat != null) {
                                        fStat = parsedStat;
                                      } else {
                                        // Try direct parseFloat if parseFStat fails
                                        const directParse = parseFloat(fStat);
                                        if (!isNaN(directParse) && isFinite(directParse)) {
                                          fStat = directParse;
                                        }
                                      }
                                    } else if (typeof fStat === 'number') {
                                      // Validate it's a finite number
                                      if (!isFinite(fStat)) fStat = null;
                                    }
                                  }
                                  if (pVal != null) {
                                    if (typeof pVal === 'string') {
                                      const parsedP = parsePValue(pVal);
                                      if (parsedP != null) {
                                        pVal = parsedP;
                                      } else {
                                        // Try direct parseFloat if parsePValue fails
                                        const directParse = parseFloat(pVal);
                                        if (!isNaN(directParse) && isFinite(directParse)) {
                                          pVal = directParse;
                                        }
                                      }
                                    } else if (typeof pVal === 'number') {
                                      // Validate it's a finite number
                                      if (!isFinite(pVal)) pVal = null;
                                    }
                                  }
                                  
                                  // Create overall object if we have at least one value
                                  // Always create it if pVal exists, even if fStat is null (we might parse it from text later)
                                  if (fStat != null || pVal != null) {
                                    overall = { 
                                      test: 'ANOVA', 
                                      stat: fStat, 
                                      p_value: pVal, 
                                      stat_label: 'F' 
                                    };
                                  }
                                }
                              } else if (s?.omnibus && typeof s.omnibus === 'object') {
                                const o = s.omnibus;
                                let p = o.p ?? o.p_value ?? o.pval ?? null;
                                let stat = o.H ?? o.F ?? o.chi2 ?? o.stat ?? o.statistic ?? null;
                                
                                // Parse if values are strings
                                if (stat != null) {
                                  const parsedStat = parseFStat(stat);
                                  if (parsedStat != null) stat = parsedStat;
                                }
                                if (p != null) {
                                  const parsedP = parsePValue(p);
                                  if (parsedP != null) p = parsedP;
                                }
                                
                                const testName = o.test || (o.H != null ? 'Kruskal–Wallis' : (o.F != null || (stat != null && typeof stat === 'number') ? 'ANOVA' : 'Omnibus'));
                                overall = { test: testName, stat, p_value: p, stat_label: (o.H != null ? 'H' : (o.F != null || (stat != null && typeof stat === 'number') ? 'F' : 'stat')) };
                              } else if (s?.overall) {
                                overall = s.overall;
                                // Try to parse stat and p_value if they're strings
                                if (overall.stat != null) {
                                  const parsedStat = parseFStat(overall.stat);
                                  if (parsedStat != null) overall.stat = parsedStat;
                                }
                                if (overall.p_value != null) {
                                  const parsedP = parsePValue(overall.p_value);
                                  if (parsedP != null) overall.p_value = parsedP;
                                }
                              } else if (s?.kruskal) {
                                overall = { test: 'Kruskal–Wallis', stat: s.kruskal.statistic, p_value: s.kruskal.p_value, stat_label: 'H' };
                              }
                            } catch {}
                            // Context for rendering
                            const alphaVal = (() => {
                              try { const a = Number((plotStats as any)?.alpha); return isFinite(a) && a > 0 ? a : 0.05; } catch { return 0.05; }
                            })(); void alphaVal;
                            const nByX = (plotStats as any)?.n_by_x && typeof (plotStats as any).n_by_x === 'object' ? (plotStats as any).n_by_x : null;
                            const groupNames = nByX ? Object.keys(nByX) : (((figureSummary as any)?.group_labels && Array.isArray((figureSummary as any).group_labels)) ? (figureSummary as any).group_labels : []);
                            const kGroups = Array.isArray(groupNames) ? groupNames.length : 0; void kGroups;
                            const approxN = (() => {
                              try { return nByX ? Object.values(nByX as any).reduce((a: any, b: any) => a + (Number(b) || 0), 0) : null; } catch { return null; }
                            })(); void approxN;
                            const hueLevels = (() => {
                              try { const m = (plotStats as any)?.n_by_hue; return (m && typeof m === 'object') ? Object.keys(m as any) : []; } catch { return []; }
                            })(); void hueLevels;
                            // For Stouffer context, count contributing category pairs
                            const stoufferPairsUsed = (() => {
                              try {
                                const pairs:any[] = (plotStats as any)?.pairwise || [];
                                let used = 0;
                                for (const r of pairs) {
                                  const p = r.p_uncorrected ?? r.p_value ?? r.p ?? null;
                                  const n1 = Number(r.n1 || 0), n2 = Number(r.n2 || 0);
                                  if (p != null && isFinite(p) && p > 0 && p < 1 && n1 >= 1 && n2 >= 1) used++;
                                }
                                return used;
                              } catch { return 0; }
                            })(); void stoufferPairsUsed;
                            let overallHue = (plotStats as any)?.overall_hue || null;
                            // Fallback: derive overall hue difference via Stouffer if not provided
                            if (!overallHue) {
                              try {
                                const pairs:any[] = (plotStats as any)?.pairwise || [];
                                const ps:number[] = [];
                                const ws:number[] = [];
                                for (const r of pairs) {
                                  const p = r.p_uncorrected ?? r.p_value ?? r.p ?? null;
                                  const n1 = Number(r.n1 || 0), n2 = Number(r.n2 || 0);
                                  if (p != null && isFinite(p) && p > 0 && p < 1) {
                                    ps.push(Number(p));
                                    ws.push(Math.sqrt(Math.max(0, n1 * n2)) || 1);
                                  }
                                }
                                if (ps.length >= 1) {
                                  const z = ps.reduce((acc, pv, i) => acc + invNorm(1 - pv) * (ws[i] || 1), 0) / Math.sqrt(ws.reduce((a, w) => a + (w||1)*(w||1), 0));
                                  const pmeta = 1 - normCdf(z);
                                  overallHue = { method: 'Overall series (Stouffer)', z, p_value: pmeta } as any;
                                }
                              } catch {}
                            }
                            const overallText = (figureSummary as any)?.statistical_annotations?.overall || null;
                            const statsDisplayOverall = (plotStats as any)?.stats_display?.overall_line || null;
                            
                            // Also check insights from currentPlot if available
                            const currentPlotInsights = (currentPlot as any)?.insightsJson || (currentPlot as any)?.insights || null;
                            let insightsText = null;
                            if (currentPlotInsights && typeof currentPlotInsights === 'object') {
                              // Try to extract text from insights array
                              if (Array.isArray(currentPlotInsights.insights)) {
                                const insightsArray = currentPlotInsights.insights;
                                for (const insight of insightsArray) {
                                  const body = insight?.body || insight?.text || '';
                                  if (typeof body === 'string' && (body.includes('F') || body.includes('ANOVA') || body.includes('p'))) {
                                    insightsText = body;
                                    break;
                                  }
                                }
                              }
                            }
                            
                            // Try to parse from text if structured data is missing or incomplete
                            // Check overallText, stats_display.overall_line, and insights text
                            const textToParse = overallText || statsDisplayOverall || insightsText;
                            // Parse from text if: no overall, or overall exists but missing stat or p_value
                            const needsTextParse = !overall || (overall.stat == null || overall.p_value == null);
                            if (needsTextParse && textToParse && typeof textToParse === 'string') {
                              try {
                                const text = textToParse;
                                // Try to extract F-statistic and p-value from text like "F2,177= 3.081, p 0.0484" or "ANOVA: F=3.081, p=0.0484" or "F2,177=3.08, p 0.048"
                                // Improved regex to handle formats: F2,177=3.08, F2,177= 3.08, F=3.08, F = 3.08
                                const fMatch = text.match(/F\d*,\d*\s*=\s*([\d.]+)|F\s*=\s*([\d.]+)/i);
                                // Improved regex to handle: p 0.048, p=0.048, p:0.048, p = 0.048
                                const pMatch = text.match(/p\s*[=:]?\s*([\d.eE+-]+)/i);
                                
                                if (fMatch || pMatch) {
                                  const fStatFromText = fMatch ? parseFloat(fMatch[1] || fMatch[2]) : null;
                                  const pValFromText = pMatch ? parseFloat(pMatch[1]) : null;
                                  
                                  // Use parsed values only if they're valid numbers
                                  const validFStat = (fStatFromText != null && isFinite(fStatFromText)) ? fStatFromText : null;
                                  const validPVal = (pValFromText != null && isFinite(pValFromText)) ? pValFromText : null;
                                  
                                  if (validFStat != null || validPVal != null) {
                                    // Determine test name from text
                                    let testName = 'ANOVA';
                                    if (text.toLowerCase().includes('kruskal')) testName = 'Kruskal–Wallis';
                                    else if (text.toLowerCase().includes('anova')) testName = 'ANOVA';
                                    else if (text.toLowerCase().includes('mann') || text.toLowerCase().includes('whitney')) testName = 'Mann–Whitney';
                                    
                                    // If we already have an overall object, merge values (don't overwrite existing values)
                                    if (overall) {
                                      overall = {
                                        ...overall,
                                        test: overall.test || testName,
                                        stat: overall.stat != null ? overall.stat : validFStat,
                                        p_value: overall.p_value != null ? overall.p_value : validPVal,
                                        stat_label: overall.stat_label || ((text.toLowerCase().includes('kruskal') || text.toLowerCase().includes('h=')) ? 'H' : 'F')
                                      };
                                    } else {
                                      overall = {
                                        test: testName,
                                        stat: validFStat,
                                        p_value: validPVal,
                                        stat_label: (text.toLowerCase().includes('kruskal') || text.toLowerCase().includes('h=')) ? 'H' : 'F'
                                      };
                                    }
                                  }
                                }
                              } catch {}
                            }
                            
                            // Extract ANOVA table data if available
                            const anovaData = (plotStats as any)?.anova;
                            const anovaOverall = anovaData?.overall;
                            const hasAnovaTable = anovaOverall && (anovaOverall.F != null || anovaOverall.p != null);
                            
                            // Don't return null if we have ANOVA table data
                            if ((!overall || (overall.stat == null && overall.p_value == null)) && !overallHue && !textToParse && !hasAnovaTable) return null;
                            const fmt = (v: any) => (typeof v === 'number' && isFinite(v)) ? Number(v).toPrecision(3) : (v ?? '');
                            const fmtP = (v: any) => (typeof v === 'number' && isFinite(v)) ? (v < 1e-3 ? Number(v).toExponential(2) : Number(v).toPrecision(3)) : (v ?? '');
                            
                            return (
                            <div className="space-y-4">
                              {/* ANOVA Results Table */}
                              {hasAnovaTable && (
                                <div className="p-4 bg-white border rounded-lg">
                                  <div className="text-muted-foreground font-semibold mb-3">ANOVA Results</div>
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-sm border-collapse">
                                      <thead>
                                        <tr className="border-b">
                                          <th className="text-left p-2 font-medium">Source</th>
                                          <th className="text-right p-2 font-medium">df</th>
                                          <th className="text-right p-2 font-medium">F</th>
                                          <th className="text-right p-2 font-medium">p-value</th>
                                          <th className="text-right p-2 font-medium">η²</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <tr className="border-b">
                                          <td className="p-2">Between Groups</td>
                                          <td className="text-right p-2 font-mono">{anovaOverall.df1 != null ? anovaOverall.df1 : '—'}</td>
                                          <td className="text-right p-2 font-mono">{anovaOverall.F != null ? fmt(typeof anovaOverall.F === 'string' ? parseFloat(anovaOverall.F) : anovaOverall.F) : '—'}</td>
                                          <td className="text-right p-2 font-mono">{anovaOverall.p != null ? fmtP(typeof anovaOverall.p === 'string' ? parseFloat(anovaOverall.p) : anovaOverall.p) : '—'}</td>
                                          <td className="text-right p-2 font-mono">{anovaOverall.eta2 != null ? fmt(typeof anovaOverall.eta2 === 'string' ? parseFloat(anovaOverall.eta2) : anovaOverall.eta2) : '—'}</td>
                                        </tr>
                                        <tr>
                                          <td className="p-2">Within Groups</td>
                                          <td className="text-right p-2 font-mono">{anovaOverall.df2 != null ? anovaOverall.df2 : '—'}</td>
                                          <td className="text-right p-2 font-mono">—</td>
                                          <td className="text-right p-2 font-mono">—</td>
                                          <td className="text-right p-2 font-mono">—</td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}
                              
                              {/* Overall difference (fallback if no table) */}
                              {!hasAnovaTable && (overall || textToParse) && (
                                <div className="p-3 bg-muted/30 rounded">
                                  <div className="text-muted-foreground font-semibold">Overall difference</div>
                                  <div className="space-y-2">
                                    <div className="font-mono">
                                      {overall ? (
                                        <>
                                          {overall.test ? `${overall.test}: ` : ''}
                                          {overall.stat !== undefined && overall.stat !== null && typeof overall.stat === 'number' && isFinite(overall.stat) ? `${overall.stat_label || 'stat'}=${fmt(overall.stat)} ` : ''}
                                          {overall.p_value !== undefined && overall.p_value !== null && typeof overall.p_value === 'number' && isFinite(overall.p_value) ? `p=${fmtP(overall.p_value)}` : ''}
                                        </>
                                      ) : (textToParse || '')}
                                    </div>
                                  </div>
                                </div>
                              )}
                              
                              {/* By Modality ANOVA Results */}
                              {anovaData?.by_modality && Array.isArray(anovaData.by_modality) && anovaData.by_modality.length > 0 && (
                                <div className="p-4 bg-white border rounded-lg">
                                  <div className="text-muted-foreground font-semibold mb-3">ANOVA by Modality</div>
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-sm border-collapse">
                                      <thead>
                                        <tr className="border-b">
                                          <th className="text-left p-2 font-medium">Modality</th>
                                          <th className="text-right p-2 font-medium">df</th>
                                          <th className="text-right p-2 font-medium">F</th>
                                          <th className="text-right p-2 font-medium">p-value</th>
                                          <th className="text-right p-2 font-medium">η²</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {anovaData.by_modality.map((item: any, idx: number) => (
                                          <tr key={idx} className={idx < anovaData.by_modality.length - 1 ? 'border-b' : ''}>
                                            <td className="p-2">{item.modality || '—'}</td>
                                            <td className="text-right p-2 font-mono">{item.df1 != null && item.df2 != null ? `${item.df1}, ${item.df2}` : '—'}</td>
                                            <td className="text-right p-2 font-mono">{item.F != null ? fmt(typeof item.F === 'string' ? parseFloat(item.F) : item.F) : '—'}</td>
                                            <td className="text-right p-2 font-mono">{item.p != null ? fmtP(typeof item.p === 'string' ? parseFloat(item.p) : item.p) : '—'}</td>
                                            <td className="text-right p-2 font-mono">{item.eta2 != null ? fmt(typeof item.eta2 === 'string' ? parseFloat(item.eta2) : item.eta2) : '—'}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {overallHue && (
                                  <div className="p-3 bg-muted/30 rounded">
                                    <div className="text-muted-foreground font-semibold">Overall series difference</div>
                                    <div className="space-y-2">
                                      <div className="font-mono">
                                        {overallHue.method ? `${overallHue.method}: ` : ''}
                                        {overallHue.z !== undefined ? `z=${fmt(overallHue.z)} ` : ''}
                                        {overallHue.p_value !== undefined ? `p=${fmtP(overallHue.p_value)}` : ''}
                                      </div>
                                      {/* Explanatory paragraphs removed for consistency across stats types */}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                            );
                          })()}

                          {/* Pairwise results */}
                          {(() => {
                            const pairs = (plotStats as any)?.pairwise || [];
                            const fallbackPairs = (figureSummary as any)?.statistical_annotations?.pairwise || [];
                            if (!Array.isArray(pairs) && !Array.isArray(fallbackPairs)) return null;
                            const rows = Array.isArray(pairs) && pairs.length > 0 ? pairs : fallbackPairs;
                            const stripN = (s: string) => String(s || '').replace(/\s*\(n\s*=\s*\d+\)\s*$/i, '').trim();
                            const measureLevels = (() => {
                              try {
                                const m = (plotStats as any)?.n_by_hue || (plotStats as any)?.n_by_x || (plotStats as any)?.n_per_measure || (plotStats as any)?.nByHue || null;
                                const keys = (m && typeof m === 'object') ? Object.keys(m as any) : [];
                                if (keys.length >= 2) return keys;
                              } catch {}
                              // Fallback: derive hue labels from Plotly traces (trace.name)
                              try {
                                const names: string[] = Array.isArray((plotlyFigure as any)?.data)
                                  ? Array.from(new Set(((plotlyFigure as any).data as any[]).map((t: any) => String(t?.name || '').trim()).filter((s: any) => typeof s === 'string' && s.length > 0))) as string[]
                                  : [];
                                if (names.length >= 2) return names.map(stripN);
                              } catch {}
                              return [];
                            })();
                            if (!rows || rows.length === 0) return null;
                            const fmt = (v: any) => (typeof v === 'number' && isFinite(v)) ? Number(v).toPrecision(3) : (v ?? '');
                            return (
                            <div className="space-y-2">
                              <div className="text-muted-foreground">Pairwise comparisons (Holm-corrected p shown where available)</div>
                              <div className="space-y-1">
                                {rows.map((pw: any, idx: number) => {
                                  const g1 = pw.a ?? pw.groupA ?? (Array.isArray(pw.groups) ? pw.groups[0] : pw.g1 ?? pw.categoryA);
                                  const g2 = pw.b ?? pw.groupB ?? (Array.isArray(pw.groups) ? pw.groups[1] : pw.g2 ?? pw.other_gene ?? pw.categoryB);
                                  const test = pw.test || (pw.mannwhitney_U !== undefined ? 'Mann–Whitney U' : (pw.t_stat !== undefined ? 't-test' : (pw.statistic !== undefined ? 'stat' : (pw.label ? '' : ''))));
                                  const statVal = pw.U ?? pw.mannwhitney_U ?? pw.stat ?? pw.statistic ?? pw.t_stat ?? pw.z;
                                  const pHolm = pw.holm_p ?? pw.p_holm ?? pw.p_adj;
                                  const pRaw = pw.p ?? pw.p_value ?? pw.p_uncorrected ?? pw.raw_p ?? pw.p_raw;
                                  const pShown = (pHolm != null && isFinite(Number(pHolm))) ? Number(pHolm) : ((pRaw != null && isFinite(Number(pRaw))) ? Number(pRaw) : null);
                                  const eff = pw.effect_size ?? pw.cliffs_delta ?? pw.delta ?? pw.effect_size_r_rb;
                                  const leftLabel = (() => {
                                    const n1 = pw.n1; const n2 = pw.n2; const nPairs = pw.n_pairs ?? pw.nPairs ?? pw.n;
                                    const labelWithN = (base: string, n: any) => {
                                      const hasN = /\(n\s*=/.test(base);
                                      if (hasN) return base.trim();
                                      const num = Number(n);
                                      return isFinite(num) ? `${base} (n=${num})` : base;
                                    };
                                    // Try to infer the x/category context for this pair (e.g., gene or x-level)
                                    const contextLabel = (() => {
                                      const candidates = [pw.x, pw.x_level, pw.xValue, pw.category_x, pw.cat, pw.level, pw.gene, pw.feature, pw.measure, pw.category, pw.GeneID];
                                      for (const c of candidates) {
                                        if (typeof c === 'string' && c.trim().length > 0) return String(c).trim();
                                      }
                                      return '';
                                    })();
                                    // Explicit pair labels when present
                                    if (g1 || g2) {
                                      const l = labelWithN(String(g1 || ''), n1);
                                      const r = labelWithN(String(g2 || ''), n2);
                                      return contextLabel ? `${contextLabel}: ${l} vs ${r}` : `${l} vs ${r}`;
                                    }
                                    // Per-gene two-measure comparison (derived from measure levels)
                                    if (contextLabel && Array.isArray(measureLevels) && measureLevels.length >= 2) {
                                      const m1 = stripN(String(measureLevels[0]));
                                      const m2 = stripN(String(measureLevels[1]));
                                      const pairSuffix = (nPairs != null && isFinite(Number(nPairs))) ? ` (n=${Number(nPairs)} pairs)` : '';
                                      return `${contextLabel}: ${m1} vs ${m2}${pairSuffix}`;
                                    }
                                    // Bracket-label fallback from figure annotations
                                    const label = pw.label || '';
                                    if (label) return contextLabel ? `${contextLabel}: ${label}` : label;
                                    // Last-resort fallbacks
                                    if (Array.isArray(measureLevels) && measureLevels.length >= 2) {
                                      const m1 = stripN(String(measureLevels[0]));
                                      const m2 = stripN(String(measureLevels[1]));
                                      const pairSuffix = (nPairs != null && isFinite(Number(nPairs))) ? ` (n=${Number(nPairs)} pairs)` : '';
                                      return contextLabel ? `${contextLabel}: ${m1} vs ${m2}${pairSuffix}` : `${m1} vs ${m2}${pairSuffix}`;
                                    }
                                    const cats = (figureSummary as any)?.group_labels;
                                    if (Array.isArray(cats) && cats.length >= 2) {
                                      const base = `${String(cats[0])} vs ${String(cats[1])}`;
                                      return contextLabel ? `${contextLabel}: ${base}` : base;
                                    }
                                    return contextLabel || 'pairwise comparison';
                                  })();
                                  return (
                                    <div key={idx} className="flex items-center justify-between p-2 bg-secondary/30 rounded">
                                      <span className="truncate mr-2">{leftLabel}</span>
                                      <span className="font-mono text-xs">
                                        {pw.label ? pw.label : (
                                          <>
                                            {test ? `${test} ` : ''}
                                            {statVal !== undefined ? `stat=${fmt(statVal)} ` : ''}
                                            {/* Show adjusted p if available; otherwise raw p only */}
                                            {(pHolm != null && isFinite(pHolm)) ? `p_adj=${fmt(pHolm)}` : ((pRaw != null && isFinite(pRaw)) ? `p=${fmt(pRaw)}` : '')}
                                            {/* Append significance tag for clarity; use alpha from stats when present */}
                                            {(() => {
                                              try {
                                                const a = Number((plotStats as any)?.alpha);
                                                const alpha = isFinite(a) && a > 0 ? a : 0.05;
                                                return pShown != null ? ((pShown < alpha) ? ' (sig)' : ' (ns)') : '';
                                              } catch { return ''; }
                                            })()}
                                            {eff !== undefined ? `, effect=${fmt(eff)}` : ''}
                                          </>
                                        )}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                            );
                          })()}

                          {/* Correlation (if present) */}
                          {(() => {
                              const corr = (plotStats as any)?.correlation || (plotStats as any)?.pearson || (plotStats as any)?.spearman || (plotStats as any)?.correlations;
                            if (!corr) return null;
                            try {
                              const items = Array.isArray(corr) ? corr : [corr];
                              const fmt = (v: any) => (typeof v === 'number' && isFinite(v)) ? Number(v).toPrecision(3) : (v ?? '');
                              return (
                                <div className="space-y-2">
                                  <div className="text-muted-foreground">Correlation</div>
                                  <div className="space-y-1">
                                    {items.map((c: any, i: number) => (
                                      <div key={i} className="flex items-center justify-between p-2 bg-secondary/30 rounded">
                                        <span className="truncate mr-2">{c?.type || c?.method || 'Correlation'}</span>
                                        <span className="font-mono text-xs">r={fmt(c?.r ?? c?.rho ?? c?.statistic)}{c?.p_value !== undefined ? `, p=${fmt(c.p_value)}` : ''}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            } catch { return null; }
                          })()}

                          {/* Group summaries (generic render) */}
                          {(() => {
                            const gs:any = (plotStats as any)?.group_summaries || (plotStats as any)?.group_stats;
                            if (!gs) return null;
                            try {
                              // Accept either object map or array form
                              if (Array.isArray(gs)) {
                                const rows:any[] = (gs as any[]).map((r) => ({
                                  ...r,
                                  ci_low: r?.ci_low ?? r?.ci95_low ?? r?.ci_95_low ?? r?.ci_low_95 ?? r?.ci_lower ?? null,
                                  ci_high: r?.ci_high ?? r?.ci95_high ?? r?.ci_95_high ?? r?.ci_high_95 ?? r?.ci_upper ?? null,
                                  se: r?.se ?? r?.sem,
                                  odds_ratio: r?.odds_ratio ?? r?.or ?? r?.OR ?? null,
                                  p: r?.p ?? r?.p_value ?? r?.pval ?? null,
                                }));
                                if (rows.length === 0) return null;
                                // Compact per-row line: <GeneID> - <variable>: median [q1, q3], n=, mean, CI
                                const formatValue = (v: any): string => {
                                  if (typeof v === 'number' && isFinite(v)) return Number(v).toPrecision(3);
                                  return String(v ?? '');
                                };
                                // Helper function to format statistical results (same as object map version)
                                const formatStatisticalResult = (v: any): { primary: string | null; secondary: Array<[string, any]> } => {
                                  const p = v.p ?? v.p_value ?? v.pval ?? v.p_val ?? null;
                                  const ciLow = v.ci_low ?? v.ci95_low ?? v.ci_95_low ?? v.ci_low_95 ?? v.ci_lower ?? v.lower_ci ?? null;
                                  const ciHigh = v.ci_high ?? v.ci95_high ?? v.ci_95_high ?? v.ci_high_95 ?? v.ci_upper ?? v.upper_ci ?? null;
                                  const n = v.n ?? v.sample_size ?? v.count ?? null;
                                  
                                  const or = v.odds_ratio ?? v.or ?? v.OR ?? v.oddsRatio ?? null;
                                  if (or != null && (ciLow != null || ciHigh != null || p != null)) {
                                    const parts: string[] = [`OR=${formatValue(or)}`];
                                    if (ciLow != null && ciHigh != null) parts.push(`(95% CI ${formatValue(ciLow)}-${formatValue(ciHigh)})`);
                                    if (p != null) parts.push(`p=${formatValue(p)}`);
                                    if (n != null) parts.push(`n=${n}`);
                                    const excluded = ['odds_ratio', 'or', 'OR', 'oddsRatio', 'p', 'p_value', 'pval', 'p_val', 'ci_low', 'ci95_low', 'ci_95_low', 'ci_low_95', 'ci_lower', 'lower_ci', 'ci_high', 'ci95_high', 'ci_95_high', 'ci_high_95', 'ci_upper', 'upper_ci', 'n', 'sample_size', 'count'];
                                    return { primary: parts.join(' '), secondary: Object.entries(v).filter(([k]) => !excluded.includes(k.toLowerCase())).map(([k, val]) => [k, val]) };
                                  }
                                  
                                  const hr = v.hazard_ratio ?? v.hr ?? v.HR ?? v.hazardRatio ?? null;
                                  if (hr != null && (ciLow != null || ciHigh != null || p != null)) {
                                    const parts: string[] = [`HR=${formatValue(hr)}`];
                                    if (ciLow != null && ciHigh != null) parts.push(`(95% CI ${formatValue(ciLow)}-${formatValue(ciHigh)})`);
                                    if (p != null) parts.push(`p=${formatValue(p)}`);
                                    if (n != null) parts.push(`n=${n}`);
                                    const excluded = ['hazard_ratio', 'hr', 'HR', 'hazardRatio', 'p', 'p_value', 'pval', 'p_val', 'ci_low', 'ci95_low', 'ci_95_low', 'ci_low_95', 'ci_lower', 'lower_ci', 'ci_high', 'ci95_high', 'ci_95_high', 'ci_high_95', 'ci_upper', 'upper_ci', 'n', 'sample_size', 'count'];
                                    return { primary: parts.join(' '), secondary: Object.entries(v).filter(([k]) => !excluded.includes(k.toLowerCase())).map(([k, val]) => [k, val]) };
                                  }
                                  
                                  const rr = v.risk_ratio ?? v.rr ?? v.RR ?? v.riskRatio ?? null;
                                  if (rr != null && (ciLow != null || ciHigh != null || p != null)) {
                                    const parts: string[] = [`RR=${formatValue(rr)}`];
                                    if (ciLow != null && ciHigh != null) parts.push(`(95% CI ${formatValue(ciLow)}-${formatValue(ciHigh)})`);
                                    if (p != null) parts.push(`p=${formatValue(p)}`);
                                    if (n != null) parts.push(`n=${n}`);
                                    const excluded = ['risk_ratio', 'rr', 'RR', 'riskRatio', 'p', 'p_value', 'pval', 'p_val', 'ci_low', 'ci95_low', 'ci_95_low', 'ci_low_95', 'ci_lower', 'lower_ci', 'ci_high', 'ci95_high', 'ci_95_high', 'ci_high_95', 'ci_upper', 'upper_ci', 'n', 'sample_size', 'count'];
                                    return { primary: parts.join(' '), secondary: Object.entries(v).filter(([k]) => !excluded.includes(k.toLowerCase())).map(([k, val]) => [k, val]) };
                                  }
                                  
                                  const md = v.mean_difference ?? v.mean_diff ?? v.md ?? v.MD ?? v.diff ?? null;
                                  const cohensD = v.cohens_d ?? v.cohensD ?? v.d ?? v.effect_size ?? v.effectSize ?? null;
                                  const hedgesG = v.hedges_g ?? v.hedgesG ?? v.g ?? null;
                                  const cliffsDelta = v.cliffs_delta ?? v.cliffsDelta ?? v.delta ?? null;
                                  if ((md != null || cohensD != null || hedgesG != null || cliffsDelta != null) && (ciLow != null || ciHigh != null || p != null)) {
                                    const parts: string[] = [];
                                    if (md != null) parts.push(`MD=${formatValue(md)}`);
                                    else if (cohensD != null) parts.push(`Cohen's d=${formatValue(cohensD)}`);
                                    else if (hedgesG != null) parts.push(`Hedges' g=${formatValue(hedgesG)}`);
                                    else if (cliffsDelta != null) parts.push(`Cliff's δ=${formatValue(cliffsDelta)}`);
                                    if (ciLow != null && ciHigh != null) parts.push(`(95% CI ${formatValue(ciLow)}-${formatValue(ciHigh)})`);
                                    if (p != null) parts.push(`p=${formatValue(p)}`);
                                    if (n != null) parts.push(`n=${n}`);
                                    const excluded = ['mean_difference', 'mean_diff', 'md', 'MD', 'diff', 'cohens_d', 'cohensD', 'd', 'effect_size', 'effectSize', 'hedges_g', 'hedgesG', 'g', 'cliffs_delta', 'cliffsDelta', 'delta', 'p', 'p_value', 'pval', 'p_val', 'ci_low', 'ci95_low', 'ci_95_low', 'ci_low_95', 'ci_lower', 'lower_ci', 'ci_high', 'ci95_high', 'ci_95_high', 'ci_high_95', 'ci_upper', 'upper_ci', 'n', 'sample_size', 'count'];
                                    return { primary: parts.join(' '), secondary: Object.entries(v).filter(([k]) => !excluded.includes(k.toLowerCase())).map(([k, val]) => [k, val]) };
                                  }
                                  
                                  const r = v.r ?? v.pearson_r ?? v.spearman_r ?? v.correlation ?? v.corr ?? null;
                                  if (r != null && (p != null || ciLow != null || ciHigh != null)) {
                                    const corrType = v.spearman_r != null ? 'ρ' : (v.kendall_r != null ? 'τ' : 'r');
                                    const parts: string[] = [`${corrType}=${formatValue(r)}`];
                                    if (ciLow != null && ciHigh != null) parts.push(`(95% CI ${formatValue(ciLow)}-${formatValue(ciHigh)})`);
                                    if (p != null) parts.push(`p=${formatValue(p)}`);
                                    if (n != null) parts.push(`n=${n}`);
                                    const excluded = ['r', 'pearson_r', 'spearman_r', 'kendall_r', 'correlation', 'corr', 'p', 'p_value', 'pval', 'p_val', 'ci_low', 'ci95_low', 'ci_95_low', 'ci_low_95', 'ci_lower', 'lower_ci', 'ci_high', 'ci95_high', 'ci_95_high', 'ci_high_95', 'ci_upper', 'upper_ci', 'n', 'sample_size', 'count'];
                                    return { primary: parts.join(' '), secondary: Object.entries(v).filter(([k]) => !excluded.includes(k.toLowerCase())).map(([k, val]) => [k, val]) };
                                  }
                                  
                                  const beta = v.beta ?? v.coefficient ?? v.coef ?? v.slope ?? null;
                                  if (beta != null && (p != null || ciLow != null || ciHigh != null)) {
                                    const parts: string[] = [`β=${formatValue(beta)}`];
                                    if (ciLow != null && ciHigh != null) parts.push(`(95% CI ${formatValue(ciLow)}-${formatValue(ciHigh)})`);
                                    if (p != null) parts.push(`p=${formatValue(p)}`);
                                    if (n != null) parts.push(`n=${n}`);
                                    const excluded = ['beta', 'coefficient', 'coef', 'slope', 'p', 'p_value', 'pval', 'p_val', 'ci_low', 'ci95_low', 'ci_95_low', 'ci_low_95', 'ci_lower', 'lower_ci', 'ci_high', 'ci95_high', 'ci_95_high', 'ci_high_95', 'ci_upper', 'upper_ci', 'n', 'sample_size', 'count'];
                                    return { primary: parts.join(' '), secondary: Object.entries(v).filter(([k]) => !excluded.includes(k.toLowerCase())).map(([k, val]) => [k, val]) };
                                  }
                                  
                                  if (p != null && (ciLow != null || ciHigh != null)) {
                                    const parts: string[] = [];
                                    if (ciLow != null && ciHigh != null) parts.push(`95% CI ${formatValue(ciLow)}-${formatValue(ciHigh)}`);
                                    parts.push(`p=${formatValue(p)}`);
                                    if (n != null) parts.push(`n=${n}`);
                                    const excluded = ['p', 'p_value', 'pval', 'p_val', 'ci_low', 'ci95_low', 'ci_95_low', 'ci_low_95', 'ci_lower', 'lower_ci', 'ci_high', 'ci95_high', 'ci_95_high', 'ci_high_95', 'ci_upper', 'upper_ci', 'n', 'sample_size', 'count'];
                                    return { primary: parts.join(', '), secondary: Object.entries(v).filter(([k]) => !excluded.includes(k.toLowerCase())).map(([k, val]) => [k, val]) };
                                  }
                                  
                                  return { primary: null, secondary: Object.entries(v) };
                                };
                                
                                const mkLine = (r: any) => {
                                  const left = [r.GeneID || r.gene || r.x || r.category].filter(Boolean)[0] || '';
                                  const varLabel = r.variable || r.series || r.hue || r.group || '';
                                  const groupName = r.group || varLabel || left || '';
                                  
                                  // Use the general statistical formatter
                                  const formatted = formatStatisticalResult(r);
                                  
                                  // If we have a primary statistical result, use it
                                  if (formatted.primary) {
                                    const parts: string[] = [];
                                    if (groupName) parts.push(`${groupName}:`);
                                    parts.push(formatted.primary);
                                    return parts.join(' ');
                                  }
                                  
                                  // Otherwise, use the standard format: median [q1, q3], n=, mean, CI
                                  const ciLow = r.ci_low ?? r.ci95_low ?? r.ci_95_low ?? r.ci_low_95 ?? r.ci_lower ?? null;
                                  const ciHigh = r.ci_high ?? r.ci95_high ?? r.ci_95_high ?? r.ci_high_95 ?? r.ci_upper ?? null;
                                  const median = formatValue(r.median);
                                  const q1 = formatValue(r.q1);
                                  const q3 = formatValue(r.q3);
                                  const nStr = (r.n != null) ? `, n=${String(r.n)}` : '';
                                  const mean = (r.mean != null) ? `, mean=${formatValue(r.mean)}` : '';
                                  const ci = (ciLow != null || ciHigh != null) ? `, CI=[${formatValue(ciLow)}, ${formatValue(ciHigh)}]` : '';
                                  const head = left ? `${left}: ` : '';
                                  return `${head}${varLabel} - median=${median} [${q1}, ${q3}]${nStr}${mean}${ci}`;
                                };
                                return (
                                  <div className="space-y-2">
                                    <div className="text-muted-foreground">Group summaries</div>
                                    <div className="space-y-1">
                                      {rows.slice(0, 50).map((r, i) => (
                                        <div key={i} className="p-2 bg-secondary/30 rounded font-mono text-xs truncate" title={mkLine(r)}>
                                          {mkLine(r)}
                                        </div>
                                      ))}
                                      {rows.length > 50 && (
                                        <div className="text-[10px] text-muted-foreground">Showing first 50 of {rows.length}</div>
                                      )}
                                    </div>
                                  </div>
                                );
                              }

                              const keys = Object.keys(gs || {});
                              if (keys.length === 0) return null;
                              const looksOverall = ('n' in gs) || ('spearman_r' in gs) || ('ols_slope' in gs) || ('median' in gs);
                              const formatValue = (v: any): string => {
                                if (typeof v === 'number' && isFinite(v)) return Number(v).toPrecision(3);
                                if (Array.isArray(v)) return v.map((x: any) => formatValue(x)).join(', ');
                                return String(v ?? '');
                              };
                              
                              // Helper function to format statistical results
                              const formatStatisticalResult = (v: any): { primary: string | null; secondary: Array<[string, any]> } => {
                                // Extract common fields
                                const p = v.p ?? v.p_value ?? v.pval ?? v.p_val ?? null;
                                const ciLow = v.ci_low ?? v.ci95_low ?? v.ci_95_low ?? v.ci_low_95 ?? v.ci_lower ?? v.lower_ci ?? null;
                                const ciHigh = v.ci_high ?? v.ci95_high ?? v.ci_95_high ?? v.ci_high_95 ?? v.ci_upper ?? v.upper_ci ?? null;
                                const n = v.n ?? v.sample_size ?? v.count ?? null;
                                
                                // Detect and format different statistical result types
                                
                                // 1. Odds Ratio (OR) - Logistic regression
                                const or = v.odds_ratio ?? v.or ?? v.OR ?? v.oddsRatio ?? null;
                                if (or != null && (ciLow != null || ciHigh != null || p != null)) {
                                  const parts: string[] = [`OR=${formatValue(or)}`];
                                  if (ciLow != null && ciHigh != null) parts.push(`(95% CI ${formatValue(ciLow)}-${formatValue(ciHigh)})`);
                                  if (p != null) parts.push(`p=${formatValue(p)}`);
                                  if (n != null) parts.push(`n=${n}`);
                                  const excluded = ['odds_ratio', 'or', 'OR', 'oddsRatio', 'p', 'p_value', 'pval', 'p_val', 'ci_low', 'ci95_low', 'ci_95_low', 'ci_low_95', 'ci_lower', 'lower_ci', 'ci_high', 'ci95_high', 'ci_95_high', 'ci_high_95', 'ci_upper', 'upper_ci', 'n', 'sample_size', 'count'];
                                  return { primary: parts.join(' '), secondary: Object.entries(v).filter(([k]) => !excluded.includes(k.toLowerCase())).map(([k, val]) => [k, val]) };
                                }
                                
                                // 2. Hazard Ratio (HR) - Survival analysis
                                const hr = v.hazard_ratio ?? v.hr ?? v.HR ?? v.hazardRatio ?? null;
                                if (hr != null && (ciLow != null || ciHigh != null || p != null)) {
                                  const parts: string[] = [`HR=${formatValue(hr)}`];
                                  if (ciLow != null && ciHigh != null) parts.push(`(95% CI ${formatValue(ciLow)}-${formatValue(ciHigh)})`);
                                  if (p != null) parts.push(`p=${formatValue(p)}`);
                                  if (n != null) parts.push(`n=${n}`);
                                  const excluded = ['hazard_ratio', 'hr', 'HR', 'hazardRatio', 'p', 'p_value', 'pval', 'p_val', 'ci_low', 'ci95_low', 'ci_95_low', 'ci_low_95', 'ci_lower', 'lower_ci', 'ci_high', 'ci95_high', 'ci_95_high', 'ci_high_95', 'ci_upper', 'upper_ci', 'n', 'sample_size', 'count'];
                                  return { primary: parts.join(' '), secondary: Object.entries(v).filter(([k]) => !excluded.includes(k.toLowerCase())).map(([k, val]) => [k, val]) };
                                }
                                
                                // 3. Risk Ratio (RR) - Epidemiology
                                const rr = v.risk_ratio ?? v.rr ?? v.RR ?? v.riskRatio ?? null;
                                if (rr != null && (ciLow != null || ciHigh != null || p != null)) {
                                  const parts: string[] = [`RR=${formatValue(rr)}`];
                                  if (ciLow != null && ciHigh != null) parts.push(`(95% CI ${formatValue(ciLow)}-${formatValue(ciHigh)})`);
                                  if (p != null) parts.push(`p=${formatValue(p)}`);
                                  if (n != null) parts.push(`n=${n}`);
                                  const excluded = ['risk_ratio', 'rr', 'RR', 'riskRatio', 'p', 'p_value', 'pval', 'p_val', 'ci_low', 'ci95_low', 'ci_95_low', 'ci_low_95', 'ci_lower', 'lower_ci', 'ci_high', 'ci95_high', 'ci_95_high', 'ci_high_95', 'ci_upper', 'upper_ci', 'n', 'sample_size', 'count'];
                                  return { primary: parts.join(' '), secondary: Object.entries(v).filter(([k]) => !excluded.includes(k.toLowerCase())).map(([k, val]) => [k, val]) };
                                }
                                
                                // 4. Mean Difference / Effect Size - t-tests, ANOVA
                                const md = v.mean_difference ?? v.mean_diff ?? v.md ?? v.MD ?? v.diff ?? null;
                                const cohensD = v.cohens_d ?? v.cohensD ?? v.d ?? v.effect_size ?? v.effectSize ?? null;
                                const hedgesG = v.hedges_g ?? v.hedgesG ?? v.g ?? null;
                                const cliffsDelta = v.cliffs_delta ?? v.cliffsDelta ?? v.delta ?? null;
                                
                                if ((md != null || cohensD != null || hedgesG != null || cliffsDelta != null) && (ciLow != null || ciHigh != null || p != null)) {
                                  const parts: string[] = [];
                                  if (md != null) parts.push(`MD=${formatValue(md)}`);
                                  else if (cohensD != null) parts.push(`Cohen's d=${formatValue(cohensD)}`);
                                  else if (hedgesG != null) parts.push(`Hedges' g=${formatValue(hedgesG)}`);
                                  else if (cliffsDelta != null) parts.push(`Cliff's δ=${formatValue(cliffsDelta)}`);
                                  
                                  if (ciLow != null && ciHigh != null) parts.push(`(95% CI ${formatValue(ciLow)}-${formatValue(ciHigh)})`);
                                  if (p != null) parts.push(`p=${formatValue(p)}`);
                                  if (n != null) parts.push(`n=${n}`);
                                  const excluded = ['mean_difference', 'mean_diff', 'md', 'MD', 'diff', 'cohens_d', 'cohensD', 'd', 'effect_size', 'effectSize', 'hedges_g', 'hedgesG', 'g', 'cliffs_delta', 'cliffsDelta', 'delta', 'p', 'p_value', 'pval', 'p_val', 'ci_low', 'ci95_low', 'ci_95_low', 'ci_low_95', 'ci_lower', 'lower_ci', 'ci_high', 'ci95_high', 'ci_95_high', 'ci_high_95', 'ci_upper', 'upper_ci', 'n', 'sample_size', 'count'];
                                  return { primary: parts.join(' '), secondary: Object.entries(v).filter(([k]) => !excluded.includes(k.toLowerCase())).map(([k, val]) => [k, val]) };
                                }
                                
                                // 5. Correlation coefficient
                                const r = v.r ?? v.pearson_r ?? v.spearman_r ?? v.correlation ?? v.corr ?? null;
                                if (r != null && (p != null || ciLow != null || ciHigh != null)) {
                                  const corrType = v.spearman_r != null ? 'ρ' : (v.kendall_r != null ? 'τ' : 'r');
                                  const parts: string[] = [`${corrType}=${formatValue(r)}`];
                                  if (ciLow != null && ciHigh != null) parts.push(`(95% CI ${formatValue(ciLow)}-${formatValue(ciHigh)})`);
                                  if (p != null) parts.push(`p=${formatValue(p)}`);
                                  if (n != null) parts.push(`n=${n}`);
                                  const excluded = ['r', 'pearson_r', 'spearman_r', 'kendall_r', 'correlation', 'corr', 'p', 'p_value', 'pval', 'p_val', 'ci_low', 'ci95_low', 'ci_95_low', 'ci_low_95', 'ci_lower', 'lower_ci', 'ci_high', 'ci95_high', 'ci_95_high', 'ci_high_95', 'ci_upper', 'upper_ci', 'n', 'sample_size', 'count'];
                                  return { primary: parts.join(' '), secondary: Object.entries(v).filter(([k]) => !excluded.includes(k.toLowerCase())).map(([k, val]) => [k, val]) };
                                }
                                
                                // 6. Regression coefficient / Beta
                                const beta = v.beta ?? v.coefficient ?? v.coef ?? v.slope ?? null;
                                if (beta != null && (p != null || ciLow != null || ciHigh != null)) {
                                  const parts: string[] = [`β=${formatValue(beta)}`];
                                  if (ciLow != null && ciHigh != null) parts.push(`(95% CI ${formatValue(ciLow)}-${formatValue(ciHigh)})`);
                                  if (p != null) parts.push(`p=${formatValue(p)}`);
                                  if (n != null) parts.push(`n=${n}`);
                                  const excluded = ['beta', 'coefficient', 'coef', 'slope', 'p', 'p_value', 'pval', 'p_val', 'ci_low', 'ci95_low', 'ci_95_low', 'ci_low_95', 'ci_lower', 'lower_ci', 'ci_high', 'ci95_high', 'ci_95_high', 'ci_high_95', 'ci_upper', 'upper_ci', 'n', 'sample_size', 'count'];
                                  return { primary: parts.join(' '), secondary: Object.entries(v).filter(([k]) => !excluded.includes(k.toLowerCase())).map(([k, val]) => [k, val]) };
                                }
                                
                                // 7. Generic: if p-value and CI are present, format as generic statistical result
                                if (p != null && (ciLow != null || ciHigh != null)) {
                                  const parts: string[] = [];
                                  if (ciLow != null && ciHigh != null) parts.push(`95% CI ${formatValue(ciLow)}-${formatValue(ciHigh)}`);
                                  parts.push(`p=${formatValue(p)}`);
                                  if (n != null) parts.push(`n=${n}`);
                                  const excluded = ['p', 'p_value', 'pval', 'p_val', 'ci_low', 'ci95_low', 'ci_95_low', 'ci_low_95', 'ci_lower', 'lower_ci', 'ci_high', 'ci95_high', 'ci_95_high', 'ci_high_95', 'ci_upper', 'upper_ci', 'n', 'sample_size', 'count'];
                                  return { primary: parts.join(', '), secondary: Object.entries(v).filter(([k]) => !excluded.includes(k.toLowerCase())).map(([k, val]) => [k, val]) };
                                }
                                
                                // No specific statistical result detected
                                return { primary: null, secondary: Object.entries(v) };
                              };
                              
                              if (looksOverall) {
                                const fields = ['n','mean','median','std','min','max','q1','q3','spearman_r','spearman_p','pearson_r','pearson_p','ols_slope','ols_intercept','ols_slope_ci'];
                                return (
                                  <div className="space-y-2">
                                    <div className="text-muted-foreground">Overall summary</div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                      {fields.filter((k) => gs[k] !== undefined).map((k) => (
                                        <div key={k} className="flex items-center justify-between p-2 bg-secondary/30 rounded">
                                          <span className="truncate mr-2">{String(k).split('_').join(' ')}</span>
                                          <span className="font-mono text-xs">{formatValue(gs[k])}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              }
                              // Otherwise, treat as map of group→summary
                              return (
                                <div className="space-y-2">
                                  <div className="text-muted-foreground">Group summaries</div>
                                  <div className="space-y-1">
                                    {keys.map((k) => {
                                      const v:any = gs[k] || {};
                                      const formatted = formatStatisticalResult(v);
                                      
                                      // If we have a primary statistical result, display it prominently
                                      if (formatted.primary) {
                                        return (
                                          <div key={k} className="p-2 bg-secondary/30 rounded">
                                            <div className="text-xs font-medium mb-1">{String(k)}</div>
                                            <div className="space-y-1">
                                              <div className="font-mono text-xs">{formatted.primary}</div>
                                              {/* Show other fields that aren't already displayed */}
                                              {formatted.secondary.length > 0 && (
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1">
                                                  {formatted.secondary.map(([kk, vv]: any) => (
                                                    <div key={kk} className="flex items-center justify-between">
                                                      <span className="text-xs text-muted-foreground mr-2">{String(kk).split('_').join(' ')}</span>
                                                      <span className="font-mono text-xs">{formatValue(vv)}</span>
                                                    </div>
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      }
                                      
                                      // Otherwise, show all fields in grid format
                                      return (
                                        <div key={k} className="p-2 bg-secondary/30 rounded">
                                          <div className="text-xs font-medium mb-1">{String(k)}</div>
                                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {Object.entries(v).map(([kk, vv]: any) => (
                                              <div key={kk} className="flex items-center justify-between">
                                                <span className="text-xs text-muted-foreground mr-2">{String(kk).split('_').join(' ')}</span>
                                                <span className="font-mono text-xs">{formatValue(vv)}</span>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            } catch { return null; }
                          })()}

                          {/* Methods list (best-effort) */}
                          {(() => {
                            const methods = new Set<string>();
                            try {
                              (plotStats.methods || plotStats.tests || plotStats.computed_tests || []).forEach((m: any) => methods.add(String(m)));
                            } catch {}
                            try { if (plotStats.overall?.test) methods.add(String(plotStats.overall.test)); } catch {}
                            try { if (plotStats.overall_hue?.method) methods.add(String(plotStats.overall_hue.method)); } catch {}
                            try {
                              (plotStats.pairwise || []).forEach((pw: any) => { if (pw?.test) methods.add(String(pw.test)); });
                            } catch {}
                            const list = Array.from(methods);
                            return list.length > 0 ? (
                              <div>
                                <div className="text-muted-foreground">Methods used</div>
                                <div className="flex flex-wrap gap-2 mt-1">
                                  {list.map((m) => (
                                    <Badge key={m} variant="outline">{m}</Badge>
                                  ))}
                                </div>
                              </div>
                            ) : null;
                          })()}

                          {/* OLS fits */}
                          {(() => {
                            const olsFits = (plotStats as any)?.ols_fits || (plotStats as any)?.ols || {};
                            if (!olsFits || typeof olsFits !== 'object' || Object.keys(olsFits).length === 0) return null;
                            const fmt = (v: any): string => {
                              if (typeof v === 'number' && isFinite(v)) {
                                if (v < 1e-3) return Number(v).toExponential(2);
                                return Number(v).toPrecision(3);
                              }
                              return String(v ?? '');
                            };
                            return (
                              <div className="space-y-2">
                                <div className="text-muted-foreground">OLS fits</div>
                                <div className="space-y-2">
                                  {Object.entries(olsFits).map(([key, fit]: any) => {
                                    if (!fit || typeof fit !== 'object') return null;
                                    return (
                                      <div key={key} className="p-2 bg-secondary/30 rounded">
                                        <div className="text-xs font-medium mb-1">{String(key)}</div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                          {fit.slope !== undefined && (
                                            <div className="flex items-center justify-between">
                                              <span className="text-xs text-muted-foreground mr-2">Slope</span>
                                              <span className="font-mono text-xs">{fmt(fit.slope)}</span>
                                            </div>
                                          )}
                                          {fit.intercept !== undefined && (
                                            <div className="flex items-center justify-between">
                                              <span className="text-xs text-muted-foreground mr-2">Intercept</span>
                                              <span className="font-mono text-xs">{fmt(fit.intercept)}</span>
                                            </div>
                                          )}
                                          {fit.r !== undefined && (
                                            <div className="flex items-center justify-between">
                                              <span className="text-xs text-muted-foreground mr-2">R</span>
                                              <span className="font-mono text-xs">{fmt(fit.r)}</span>
                                            </div>
                                          )}
                                          {(fit.p !== undefined || fit.p_value !== undefined) && (
                                            <div className="flex items-center justify-between">
                                              <span className="text-xs text-muted-foreground mr-2">P</span>
                                              <span className="font-mono text-xs">{fmt(fit.p ?? fit.p_value)}</span>
                                            </div>
                                          )}
                                          {(fit.stderr !== undefined || fit.stderr_slope !== undefined) && (
                                            <div className="flex items-center justify-between">
                                              <span className="text-xs text-muted-foreground mr-2">Stderr</span>
                                              <span className="font-mono text-xs">{fmt(fit.stderr ?? fit.stderr_slope)}</span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })()}

                          {/* Pearson correlations */}
                          {(() => {
                            const pearsonCorrs = (plotStats as any)?.pearson_corrs || (plotStats as any)?.pearson || {};
                            if (!pearsonCorrs || typeof pearsonCorrs !== 'object' || Object.keys(pearsonCorrs).length === 0) return null;
                            const fmt = (v: any): string => {
                              if (typeof v === 'number' && isFinite(v)) {
                                if (v < 1e-3) return Number(v).toExponential(2);
                                return Number(v).toPrecision(3);
                              }
                              return String(v ?? '');
                            };
                            return (
                              <div className="space-y-2">
                                <div className="text-muted-foreground">Pearson correlations</div>
                                <div className="space-y-2">
                                  {Object.entries(pearsonCorrs).map(([key, corr]: any) => {
                                    if (!corr || typeof corr !== 'object') return null;
                                    return (
                                      <div key={key} className="p-2 bg-secondary/30 rounded">
                                        <div className="text-xs font-medium mb-1">{String(key)}</div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                          {corr.r !== undefined && (
                                            <div className="flex items-center justify-between">
                                              <span className="text-xs text-muted-foreground mr-2">R</span>
                                              <span className="font-mono text-xs">{fmt(corr.r)}</span>
                                            </div>
                                          )}
                                          {(corr.p !== undefined || corr.p_value !== undefined) && (
                                            <div className="flex items-center justify-between">
                                              <span className="text-xs text-muted-foreground mr-2">P</span>
                                              <span className="font-mono text-xs">{fmt(corr.p ?? corr.p_value)}</span>
                                            </div>
                                          )}
                                          {(corr.ci_low !== undefined || corr.ci_low_95 !== undefined) && (
                                            <div className="flex items-center justify-between">
                                              <span className="text-xs text-muted-foreground mr-2">CI Low</span>
                                              <span className="font-mono text-xs">{fmt(corr.ci_low ?? corr.ci_low_95)}</span>
                                            </div>
                                          )}
                                          {(corr.ci_high !== undefined || corr.ci_high_95 !== undefined) && (
                                            <div className="flex items-center justify-between">
                                              <span className="text-xs text-muted-foreground mr-2">CI High</span>
                                              <span className="font-mono text-xs">{fmt(corr.ci_high ?? corr.ci_high_95)}</span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })()}

                          {/* Logistic Regression Results */}
                          {(() => {
                            const logisticReg = (plotStats as any)?.logistic_regression || (plotStats as any)?.logisticRegression || (plotStats as any)?.logistic || {};
                            if (!logisticReg || typeof logisticReg !== 'object' || Object.keys(logisticReg).length === 0) {
                              // Continue to ANCOVA
                            } else {
                              // Check if it's in array format (predictors, odds_ratio, ci_lower, etc. as arrays)
                              const predictors = logisticReg.predictors || logisticReg.predictor || [];
                              const oddsRatios = logisticReg.odds_ratio || logisticReg.oddsRatio || logisticReg.or || [];
                              const ciLower = logisticReg.ci_lower || logisticReg.ciLower || logisticReg.ci_low || [];
                              const ciUpper = logisticReg.ci_upper || logisticReg.ciUpper || logisticReg.ci_high || [];
                              const pValues = logisticReg.p_value || logisticReg.pValue || logisticReg.p || [];
                              const coefs = logisticReg.coef || logisticReg.coefficient || logisticReg.beta || [];
                              const ses = logisticReg.se || logisticReg.stderr || logisticReg.standard_error || [];
                              
                              // If we have predictors array, format as table
                              if (Array.isArray(predictors) && predictors.length > 0) {
                                const fmt = (v: any): string => {
                                  if (typeof v === 'number' && isFinite(v)) {
                                    if (v < 1e-3) return Number(v).toExponential(2);
                                    return Number(v).toPrecision(3);
                                  }
                                  return String(v ?? '');
                                };
                                
                                // Build rows
                                const rows = predictors.map((pred: any, idx: number) => ({
                                  predictor: String(pred || `Predictor ${idx + 1}`),
                                  odds_ratio: oddsRatios[idx],
                                  ci_lower: ciLower[idx],
                                  ci_upper: ciUpper[idx],
                                  p_value: pValues[idx],
                                  coef: coefs[idx],
                                  se: ses[idx],
                                }));
                                
                                // Determine columns to show
                                const hasOR = oddsRatios.length > 0 && oddsRatios.some((v: any) => v != null);
                                const hasCI = (ciLower.length > 0 && ciLower.some((v: any) => v != null)) || 
                                            (ciUpper.length > 0 && ciUpper.some((v: any) => v != null));
                                const hasP = pValues.length > 0 && pValues.some((v: any) => v != null);
                                const hasCoef = coefs.length > 0 && coefs.some((v: any) => v != null);
                                const hasSE = ses.length > 0 && ses.some((v: any) => v != null);
                                
                                const columns: Array<{ key: string; label: string; show: boolean }> = [
                                  { key: 'predictor', label: 'Predictor', show: true },
                                  { key: 'odds_ratio', label: 'Odds Ratio', show: hasOR },
                                  { key: 'ci_lower', label: 'CI Lower', show: hasCI },
                                  { key: 'ci_upper', label: 'CI Upper', show: hasCI },
                                  { key: 'p_value', label: 'P Value', show: hasP },
                                  { key: 'coef', label: 'Coef', show: hasCoef },
                                  { key: 'se', label: 'SE', show: hasSE },
                                ].filter(col => col.show);
                                
                                return (
                                  <div className="space-y-2">
                                    <div className="text-muted-foreground">Logistic Regression</div>
                                    <div className="overflow-x-auto">
                                      <table className="min-w-full text-xs border-collapse">
                                        <thead>
                                          <tr className="border-b">
                                            {columns.map((col) => (
                                              <th key={col.key} className="text-left px-2 py-1 text-muted-foreground font-medium">
                                                {col.label}
                                              </th>
                                            ))}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {rows.map((row: any, idx: number) => (
                                            <tr key={idx} className="border-b">
                                              {columns.map((col) => {
                                                const val = row[col.key];
                                                return (
                                                  <td key={col.key} className="px-2 py-1 font-mono">
                                                    {val != null ? fmt(val) : '—'}
                                                  </td>
                                                );
                                              })}
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                );
                              }
                            }
                            return null;
                          })()}

                          {/* Clustering / Dendrogram Results */}
                          {(() => {
                            const clustering = (plotStats as any)?.clustering || (plotStats as any)?.dendrogram || {};
                            const hasClustering = clustering && typeof clustering === 'object' && Object.keys(clustering).length > 0;
                            const nGenes = (plotStats as any)?.n_genes;
                            const geneLabels = (plotStats as any)?.gene_labels;
                            
                            if (!hasClustering && nGenes == null && !geneLabels) return null;
                            
                            const fmt = (v: any): string => {
                              if (typeof v === 'number' && isFinite(v)) {
                                if (v < 1e-3) return Number(v).toExponential(2);
                                return Number(v).toPrecision(3);
                              }
                              return String(v ?? '');
                            };
                            
                            return (
                              <div className="space-y-2">
                                <div className="text-muted-foreground">Clustering Analysis</div>
                                <div className="p-2 bg-secondary/30 rounded">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {/* Clustering parameters */}
                                    {clustering.method && (
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-muted-foreground mr-2">Method</span>
                                        <span className="font-mono text-xs">{String(clustering.method)}</span>
                                      </div>
                                    )}
                                    {clustering.distance_metric && (
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-muted-foreground mr-2">Distance Metric</span>
                                        <span className="font-mono text-xs">{String(clustering.distance_metric)}</span>
                                      </div>
                                    )}
                                    {(nGenes != null || clustering.n_genes != null) && (
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-muted-foreground mr-2">Number of Genes</span>
                                        <span className="font-mono text-xs">{nGenes ?? clustering.n_genes}</span>
                                      </div>
                                    )}
                                    {clustering.n_patients && (
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-muted-foreground mr-2">Number of Patients</span>
                                        <span className="font-mono text-xs">{clustering.n_patients}</span>
                                      </div>
                                    )}
                                    {/* Clustering quality metrics */}
                                    {clustering.cophenetic_correlation != null && (
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-muted-foreground mr-2">Cophenetic Correlation</span>
                                        <span className="font-mono text-xs">{fmt(clustering.cophenetic_correlation)}</span>
                                      </div>
                                    )}
                                    {clustering.max_clustering_distance != null && (
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-muted-foreground mr-2">Max Distance</span>
                                        <span className="font-mono text-xs">{fmt(clustering.max_clustering_distance)}</span>
                                      </div>
                                    )}
                                    {clustering.mean_clustering_distance != null && (
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-muted-foreground mr-2">Mean Distance</span>
                                        <span className="font-mono text-xs">{fmt(clustering.mean_clustering_distance)}</span>
                                      </div>
                                    )}
                                    {clustering.median_clustering_distance != null && (
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-muted-foreground mr-2">Median Distance</span>
                                        <span className="font-mono text-xs">{fmt(clustering.median_clustering_distance)}</span>
                                      </div>
                                    )}
                                    {/* Filtering info */}
                                    {clustering.filtering && typeof clustering.filtering === 'object' && (
                                      <>
                                        {clustering.filtering.timepoint && (
                                          <div className="flex items-center justify-between">
                                            <span className="text-xs text-muted-foreground mr-2">Timepoint</span>
                                            <span className="font-mono text-xs">{String(clustering.filtering.timepoint)}</span>
                                          </div>
                                        )}
                                        {clustering.filtering.treatment_group && (
                                          <div className="flex items-center justify-between">
                                            <span className="text-xs text-muted-foreground mr-2">Treatment Group</span>
                                            <span className="font-mono text-xs">{String(clustering.filtering.treatment_group)}</span>
                                          </div>
                                        )}
                                      </>
                                    )}
                                    {/* Expression summary */}
                                    {clustering.expression_summary && clustering.expression_summary.genes_with_zero_variance != null && (
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-muted-foreground mr-2">Genes with Zero Variance</span>
                                        <span className="font-mono text-xs">{clustering.expression_summary.genes_with_zero_variance}</span>
                                      </div>
                                    )}
                                  </div>
                                  {/* Gene labels (if present and not too many) */}
                                  {geneLabels && Array.isArray(geneLabels) && geneLabels.length > 0 && geneLabels.length <= 50 && (
                                    <div className="mt-3 pt-3 border-t">
                                      <div className="text-xs text-muted-foreground mb-1">Gene Labels ({geneLabels.length})</div>
                                      <div className="flex flex-wrap gap-1">
                                        {geneLabels.map((label: any, idx: number) => (
                                          <span key={idx} className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded">
                                            {String(label)}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {geneLabels && Array.isArray(geneLabels) && geneLabels.length > 50 && (
                                    <div className="mt-3 pt-3 border-t">
                                      <div className="text-xs text-muted-foreground mb-1">Gene Labels ({geneLabels.length} total)</div>
                                      <div className="text-xs text-muted-foreground italic">
                                        Showing first 20: {geneLabels.slice(0, 20).map((l: any) => String(l)).join(', ')}...
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })()}

                          {/* ANCOVA */}
                          {(() => {
                            const ancova = (plotStats as any)?.ancova || {};
                            if (!ancova || typeof ancova === 'object' || Object.keys(ancova).length === 0) return null;
                            const fmt = (v: any): string => {
                              if (typeof v === 'number' && isFinite(v)) {
                                if (v < 1e-3) return Number(v).toExponential(2);
                                return Number(v).toPrecision(3);
                              }
                              return String(v ?? '');
                            };
                            return (
                              <div className="space-y-2">
                                <div className="text-muted-foreground">ANCOVA</div>
                                <div className="p-2 bg-secondary/30 rounded">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {ancova.F !== undefined && (
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-muted-foreground mr-2">F</span>
                                        <span className="font-mono text-xs">{fmt(ancova.F)}</span>
                                      </div>
                                    )}
                                    {(ancova.p !== undefined || ancova.p_value !== undefined) && (
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-muted-foreground mr-2">P</span>
                                        <span className="font-mono text-xs">{fmt(ancova.p ?? ancova.p_value)}</span>
                                      </div>
                                    )}
                                    {(ancova.eta2 !== undefined || ancova.eta_squared !== undefined) && (
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-muted-foreground mr-2">Eta²</span>
                                        <span className="font-mono text-xs">{fmt(ancova.eta2 ?? ancova.eta_squared)}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}

                          {/* Additional statistics (generic renderer for unknown keys) */}
                          {(() => {
                            try {
                              const s: any = plotStats || {};
                              const known = new Set<string>([
                                // Core structure
                                'alpha','n','n_points','methods','tests','computed_tests',
                                // Sample size & grouping (n_by_* patterns excluded separately)
                                'n_by_x','n_by_hue','n_per_group','group_counts','groupCounts',
                                'species_order','group_order','category_order','legend_order',
                                // Descriptive statistics
                                'group_summaries','group_stats',
                                // Inferential statistics
                                'omnibus','overall','overall_hue','kruskal',
                                'pairwise','pairwise_kept','pairwise_mode',
                                'ttest_ind','ttest_rel','welch','mannwhitney','wilcoxon',
                                'anova','rm_anova','lmm','chi2','fisher',
                                'ks_2samp','anderson','permutation','logistic','logistic_regression','logisticRegression','poisson','negbin','zinb','coxph','gam',
                                // Effect sizes
                                'cohens_d','hedges_g','glass_delta','cliffs_delta','r_rb','odds_ratio','eta2','partial_eta2','r','r_squared',
                                'effect_size_interpretation','min_effect',
                                // Correlation & association
                                'correlation','pearson','spearman','kendall','cramers_v','phi','contingency_coefficient',
                                // Trend analysis
                                'trend','trend_summaries','regression',
                                // Multiple comparisons
                                'multiple_comparisons','top_k_pairs','top_k_effects',
                                // Display & formatting
                                'stats_display','statistical_annotations','ci_method','ci_level','ci','show_ci',
                                // Missing data & quality
                                'n_missing','n_present','missing_rate','imputation_method',
                                'outlier_count','outlier_method','duplicate_count','data_quality_score',
                                // Domain-specific (bioinformatics)
                                'log2_fc','adj_p_value','base_mean','enrichment_score',
                                // Domain-specific (time series)
                                'autocorrelation','seasonality','trend_strength',
                                // Domain-specific (clinical)
                                'hazard_ratio','survival_curves','adverse_events',
                                // Visualization-specific
                                'effect_sizes','meta_analysis','heterogeneity','neg_log10_p','significance_threshold',
                                'clustering_method','distance_metric','dendrogram_order','clustering','dendrogram','n_genes','gene_labels'
                              ]);
                              // Exclude all n_by_* patterns (they're displayed in the main stats section above)
                              const extra = Object.entries(s).filter(([k, v]) => {
                                const key = String(k);
                                return !known.has(key) && !key.startsWith('n_by_') && v !== null && v !== undefined && (typeof v !== 'object' || Object.keys(v || {}).length > 0);
                              });
                              if (extra.length === 0) return null;
                              const isArrayOfObjects = (arr: any[]): boolean => Array.isArray(arr) && arr.length > 0 && arr.every((r) => r && typeof r === 'object' && !Array.isArray(r));
                              
                              // Recursively format values, handling nested objects
                              const fmt = (v: any, depth: number = 0): string => {
                                if (depth > 3) return '[too deep]'; // Prevent infinite recursion
                                if (typeof v === 'number' && isFinite(v)) return v < 1e-3 ? v.toExponential(2) : Number(v).toPrecision(3);
                                if (Array.isArray(v)) {
                                  if (v.length === 0) return '[]';
                                  // Check if array contains objects
                                  if (v.every((x) => x && typeof x === 'object' && !Array.isArray(x))) {
                                    return `[${v.length} object${v.length !== 1 ? 's' : ''}]`;
                                  }
                                  return v.map((x) => (typeof x === 'number' && isFinite(x)) ? Number(x).toPrecision(3) : fmt(x, depth + 1)).join(', ');
                                }
                                if (v === true) return 'true';
                                if (v === false) return 'false';
                                if (v == null) return '';
                                // Handle objects - serialize to JSON for display
                                if (typeof v === 'object') {
                                  try {
                                    const serialized = JSON.stringify(v, (key, val) => {
                                      // Format numbers in JSON
                                      if (typeof val === 'number' && isFinite(val)) {
                                        return val < 1e-3 ? val.toExponential(2) : Number(val.toPrecision(3));
                                      }
                                      return val;
                                    }, 2);
                                    // Truncate very long JSON strings
                                    return serialized.length > 500 ? serialized.substring(0, 500) + '...' : serialized;
                                  } catch {
                                    return '[object]';
                                  }
                                }
                                return String(v);
                              };
                              
                              // Recursively render nested objects
                              const renderObject = (obj: any, depth: number = 0): React.ReactNode => {
                                if (depth > 3) return <span className="text-xs text-muted-foreground">[too deep]</span>;
                                const entries = Object.entries(obj || {});
                                if (entries.length === 0) return null;
                                return (
                                  <div className={`space-y-1 ${depth > 0 ? 'ml-2 pl-2 border-l' : ''}`}>
                                    {entries.map(([kk, vv]: any) => {
                                      const isNestedObject = vv && typeof vv === 'object' && !Array.isArray(vv) && vv.constructor === Object;
                                      const isNestedArray = Array.isArray(vv) && vv.length > 0 && vv.every((x: any) => x && typeof x === 'object' && !Array.isArray(x));
                                      
                                      return (
                                        <div key={String(kk)} className={isNestedObject || isNestedArray ? 'space-y-1' : 'flex items-center justify-between'}>
                                          <span className="text-xs text-muted-foreground mr-2 font-medium">{String(kk).split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</span>
                                          {isNestedObject ? (
                                            <div className="mt-1">
                                              {renderObject(vv, depth + 1)}
                                            </div>
                                          ) : isNestedArray ? (
                                            <div className="mt-1 space-y-1">
                                              {vv.slice(0, 5).map((item: any, idx: number) => (
                                                <div key={idx} className="pl-2 border-l-2 border-muted">
                                                  {renderObject(item, depth + 1)}
                                                </div>
                                              ))}
                                              {vv.length > 5 && (
                                                <div className="text-[10px] text-muted-foreground pl-2">... and {vv.length - 5} more</div>
                                              )}
                                            </div>
                                          ) : (
                                            <span className="font-mono text-xs break-all">{fmt(vv, depth)}</span>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              };
                              return (
                                <div className="space-y-3">
                                  <div className="text-muted-foreground">Additional statistics</div>
                                  {extra.map(([key, val]: any) => (
                                    <div key={String(key)} className="p-2 bg-secondary/20 rounded">
                                      <div className="text-xs font-medium mb-1">{String(key).split('_').join(' ')}</div>
                                      {/* Arrays of objects → table */}
                                      {Array.isArray(val) && isArrayOfObjects(val) ? (
                                        (() => {
                                          const rows:any[] = val as any[];
                                          const cols = Array.from(new Set(rows.flatMap((r) => Object.keys(r || {}))));
                                          return (
                                            <div className="overflow-x-auto">
                                              <table className="min-w-max text-xs">
                                                <thead>
                                                  <tr>
                                                    {cols.map((c) => (
                                                      <th key={String(c)} className="text-left px-2 py-1 text-muted-foreground">{String(c).split('_').join(' ')}</th>
                                                    ))}
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {rows.slice(0, 100).map((r, i) => (
                                                    <tr key={i} className="border-t">
                                                      {cols.map((c) => (
                                                        <td key={String(c)} className="px-2 py-1 font-mono">{fmt((r as any)[c])}</td>
                                                      ))}
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </table>
                                              {rows.length > 100 && (
                                                <div className="text-[10px] text-muted-foreground mt-1">Showing first 100 of {rows.length} rows</div>
                                              )}
                                            </div>
                                          );
                                        })()
                                      ) : Array.isArray(val) ? (
                                        <div className="font-mono text-xs">{fmt(val)}</div>
                                      ) : (typeof val === 'object') ? (
                                        renderObject(val)
                                      ) : (
                                        <div className="font-mono text-xs">{fmt(val)}</div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              );
                            } catch { return null; }
                          })()}
                          </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-sm text-muted-foreground">No stats.json available yet for this plot.</div>
                      )}
                    </div>
                    {/* Basic Statistics for each numeric column */}
                    {(() => {
                      const numerical: any = (statisticalAnalysis && (statisticalAnalysis as any).numerical) || (statisticalAnalysis ?? {});
                      const cols = dataSummary?.numericColumns || Object.keys(numerical || {});
                      return cols.map((col: string) => {
                        const stats:any = (numerical && typeof numerical === 'object') ? (numerical as any)[col] || {} : {};
                        if (!stats || Object.keys(stats).length === 0) return null;
                        return (
                          <div key={col} className="p-4 bg-muted/30 rounded-lg space-y-2">
                            <h4 className="font-medium text-sm">{col}</h4>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                              {stats.mean !== undefined && (
                                <div>
                                  <span className="text-muted-foreground">Mean</span>
                                  <p className="font-mono">{typeof stats.mean === 'number' ? stats.mean.toFixed(2) : stats.mean}</p>
                                </div>
                              )}
                              {stats.median !== undefined && (
                                <div>
                                  <span className="text-muted-foreground">Median</span>
                                  <p className="font-mono">{typeof stats.median === 'number' ? stats.median.toFixed(2) : stats.median}</p>
                                </div>
                              )}
                              {stats.std !== undefined && (
                                <div>
                                  <span className="text-muted-foreground">Std Dev</span>
                                  <p className="font-mono">{typeof stats.std === 'number' ? stats.std.toFixed(2) : stats.std}</p>
                                </div>
                              )}
                              {stats.min !== undefined && (
                                <div>
                                  <span className="text-muted-foreground">Min</span>
                                  <p className="font-mono">{typeof stats.min === 'number' ? stats.min.toFixed(2) : stats.min}</p>
                                </div>
                              )}
                              {stats.max !== undefined && (
                                <div>
                                  <span className="text-muted-foreground">Max</span>
                                  <p className="font-mono">{typeof stats.max === 'number' ? stats.max.toFixed(2) : stats.max}</p>
                                </div>
                              )}
                              {stats.q1 !== undefined && (
                                <div>
                                  <span className="text-muted-foreground">Q1 (25%)</span>
                                  <p className="font-mono">{typeof stats.q1 === 'number' ? stats.q1.toFixed(2) : stats.q1}</p>
                                </div>
                              )}
                              {stats.q3 !== undefined && (
                                <div>
                                  <span className="text-muted-foreground">Q3 (75%)</span>
                                  <p className="font-mono">{typeof stats.q3 === 'number' ? stats.q3.toFixed(2) : stats.q3}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      });
                    })()}

                    {/* Pattern Detection Results */}
                    {statisticalAnalysis && (statisticalAnalysis as any).patterns && (
                      <div className="p-4 bg-primary/5 rounded-lg space-y-2">
                        <h4 className="font-medium text-sm">Detected Patterns</h4>
                        <div className="space-y-1 text-sm">
                          {((statisticalAnalysis as any).patterns || {}).hasTimeSeries && (
                            <div className="flex items-center gap-2">
                              <Check className="h-4 w-4 text-green-500" />
                              <span>Time series data detected</span>
                            </div>
                          )}
                          {((statisticalAnalysis as any).patterns || {}).hasGroups && (
                            <div className="flex items-center gap-2">
                              <Check className="h-4 w-4 text-green-500" />
                              <span>Categorical groups found</span>
                            </div>
                          )}
                          {Array.isArray(((statisticalAnalysis as any).patterns || {}).correlations) && (statisticalAnalysis as any).patterns.correlations.length > 0 && (
                            <div className="flex items-center gap-2">
                              <Check className="h-4 w-4 text-green-500" />
                              <span>{((statisticalAnalysis as any).patterns.correlations || []).length} significant correlations detected</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Statistical Test Results extracted from plot annotations */}
                    {statisticalAnalysis && Array.isArray((statisticalAnalysis as any).testResults) && (statisticalAnalysis as any).testResults.length > 0 && (
                      <div className="p-4 bg-secondary/30 rounded-lg space-y-2">
                        <h4 className="font-medium text-sm">Statistical Tests</h4>
                        <div className="space-y-1 text-sm">
                          {(statisticalAnalysis as any).testResults.map((tr: any, idx: number) => (
                            <div key={idx} className="flex items-center justify-between">
                              <span className="text-muted-foreground truncate mr-2">{tr.text}</span>
                              <span className="font-mono text-xs">
                                {tr.pValue !== undefined ? `p=${tr.pValue}` : ''}
                                {tr.effectSize !== undefined ? `  η²=${tr.effectSize}` : ''}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Suggested Visualizations */}
                    {statisticalAnalysis && (statisticalAnalysis as any).suggestedPlots && (
                      <div className="p-4 bg-secondary/50 rounded-lg space-y-2">
                        <h4 className="font-medium text-sm">Recommended Visualizations</h4>
                        <div className="flex flex-wrap gap-2">
                          {(statisticalAnalysis as any).suggestedPlots.map((plot: string) => (
                            <Badge key={plot} variant="secondary">
                              {plot}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <TrendingUp className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No statistical analysis available yet</p>
                    <p className="text-sm mt-2">Chat with Lumi to analyze your data</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* File Upload Modal */}
      {showUpload && currentProject && (
        <FileUpload
          projectId={currentProject.id}
          onClose={() => setShowUpload(false)}
          onFileUploaded={handleFileUploaded}
        />
      )}
    </div>
  );
}